/**
 * Mock Data Service - Generates realistic demo data for Financial Hub
 *
 * Provides comprehensive mock data for suppliers, purchases, and payments
 * All CRUD operations are fully functional in mock mode
 */
import { InvoiceStatus } from '../types/invoice';
import { MemoStatus } from '../types/memo';
// Mock Data Storage Keys
const STORAGE_KEYS = {
    MOCK_MODE: 'financialHub_mockMode',
    SUPPLIERS: 'financialHub_mockSuppliers',
    DIAMONDS: 'financialHub_mockDiamonds',
    PURCHASES: 'financialHub_mockPurchases',
    PAYMENTS: 'financialHub_mockPayments',
    CUSTOMER_PAYMENTS: 'financialHub_mockCustomerPayments',
    INVOICES: 'financialHub_mockInvoices',
    MEMOS: 'financialHub_mockMemos',
    CONTACTS: 'financialHub_mockContacts',
    COUNTERS: 'financialHub_mockCounters',
    // Auction keys
    AUCTIONS: 'auctionHub_mockAuctions',
    AUCTION_BIDS: 'auctionHub_mockBids',
    AUCTION_SUMMARIES: 'auctionHub_mockSummaries',
    // Shipping keys
    SHIPPING_MOCK_MODE: 'ifsShipping_mockMode',
    SHIPPING_SHIPMENTS: 'ifsShipping_shipments',
};
// Sample company names for realistic suppliers
const COMPANY_NAMES = [
    'Diamond Direct Wholesale', 'Brilliant Gems International', 'Premier Stone Suppliers',
    'Luxury Wholesale Inc', 'Crystal Clear Diamonds', 'Royal Gem Trading',
    'Elite Diamond Corp', 'Sparkle Stones Ltd', 'Prestige Gem Company',
    'Global Diamond Network', 'Radiant Jewels Supply', 'Crown Diamond Traders'
];
const CONTACT_FIRST_NAMES = ['John', 'Sarah', 'Michael', 'Emily', 'David', 'Jennifer', 'Robert', 'Lisa', 'James', 'Mary'];
const CONTACT_LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
const CITIES = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'Miami'];
const STATES = ['NY', 'CA', 'IL', 'TX', 'AZ', 'PA', 'TX', 'CA', 'TX', 'FL'];
class MockDataService {
    constructor() {
        this.initializeCounters();
    }
    static getInstance() {
        if (!MockDataService.instance) {
            MockDataService.instance = new MockDataService();
        }
        return MockDataService.instance;
    }
    // ============================================
    // Mock Mode Management
    // ============================================
    isMockMode() {
        return localStorage.getItem(STORAGE_KEYS.MOCK_MODE) === 'true';
    }
    enableMockMode() {
        localStorage.setItem(STORAGE_KEYS.MOCK_MODE, 'true');
        this.seedInitialData();
    }
    disableMockMode() {
        localStorage.setItem(STORAGE_KEYS.MOCK_MODE, 'false');
        this.clearAllData();
    }
    toggleMockMode() {
        const newMode = !this.isMockMode();
        if (newMode) {
            this.enableMockMode();
        }
        else {
            this.disableMockMode();
        }
        return newMode;
    }
    // ============================================
    // Data Initialization
    // ============================================
    initializeCounters() {
        const stored = localStorage.getItem(STORAGE_KEYS.COUNTERS);
        if (!stored) {
            const counters = {
                supplierId: 1,
                diamondId: 1,
                purchaseId: 1,
                paymentId: 1,
                customerPaymentId: 1,
                invoiceId: 1,
                memoId: 1,
                contactId: 1,
                purchaseNumber: 1000,
                invoiceNumber: 2000,
                auctionId: 1,
                bidId: 1,
            };
            localStorage.setItem(STORAGE_KEYS.COUNTERS, JSON.stringify(counters));
        }
    }
    getCounters() {
        const stored = localStorage.getItem(STORAGE_KEYS.COUNTERS);
        const defaultCounters = {
            supplierId: 1,
            diamondId: 1,
            purchaseId: 1,
            paymentId: 1,
            customerPaymentId: 1,
            invoiceId: 1,
            memoId: 1,
            contactId: 1,
            purchaseNumber: 1000,
            invoiceNumber: 2000,
            auctionId: 1,
            bidId: 1,
        };
        if (!stored)
            return defaultCounters;
        // Merge stored counters with defaults (handles missing fields from old versions)
        const storedCounters = JSON.parse(stored);
        return {
            ...defaultCounters,
            ...storedCounters
        };
    }
    updateCounters(counters) {
        localStorage.setItem(STORAGE_KEYS.COUNTERS, JSON.stringify(counters));
    }
    clearAllData() {
        localStorage.removeItem(STORAGE_KEYS.SUPPLIERS);
        localStorage.removeItem(STORAGE_KEYS.DIAMONDS);
        localStorage.removeItem(STORAGE_KEYS.PURCHASES);
        localStorage.removeItem(STORAGE_KEYS.PAYMENTS);
        localStorage.removeItem(STORAGE_KEYS.CUSTOMER_PAYMENTS);
        localStorage.removeItem(STORAGE_KEYS.INVOICES);
        localStorage.removeItem(STORAGE_KEYS.MEMOS);
        localStorage.removeItem(STORAGE_KEYS.CONTACTS);
        localStorage.removeItem(STORAGE_KEYS.AUCTIONS);
        localStorage.removeItem(STORAGE_KEYS.AUCTION_BIDS);
        localStorage.removeItem(STORAGE_KEYS.AUCTION_SUMMARIES);
        localStorage.removeItem(STORAGE_KEYS.COUNTERS);
        this.initializeCounters();
    }
    // ============================================
    // Seed Initial Data
    // ============================================
    seedInitialData() {
        console.log('🌱 Seeding realistic 2-month business data...');
        // Generate 12 suppliers (3-4 are "preferred" with more volume)
        const suppliers = this.generateSuppliers(12);
        this.saveSuppliers(suppliers);
        // Generate ~150 mock diamonds (realistic inventory)
        const diamonds = this.generateRealisticDiamonds(150);
        this.saveDiamonds(diamonds);
        // Generate ~60 purchases over 60 days (now linked to real mock diamonds)
        const purchases = this.generateRealisticPurchases(suppliers, diamonds, 60);
        // FIX #3: COD purchases get instant payments
        const purchasesWithCOD = this.addCODAutoPayments(purchases, suppliers);
        this.savePurchases(purchasesWithCOD);
        // FIX #2: Generate ~50 payments (some purchases have multiple partial payments)
        const payments = this.generateRealisticPaymentsWithPartials(purchasesWithCOD, suppliers);
        this.savePayments(payments);
        // Re-fetch purchases after payments update them
        const updatedPurchases = this.getPurchases();
        // Generate ~31 memos over 60 days (2-3 per week)
        const memos = this.generateRealisticMemos(60);
        this.saveMemos(memos);
        // Generate ~20 invoices from approved memos (60% conversion rate)
        const invoices = this.generateRealisticInvoices(memos);
        this.saveInvoices(invoices);
        // FIX #1: Generate customer payments for paid invoices
        const customerPayments = this.generateCustomerPayments(invoices);
        this.saveCustomerPayments(customerPayments);
        // FIX #4: Add edge cases (1 bounced payment, 1 cancelled invoice, 1 denied dealer)
        this.addEdgeCases(updatedPurchases, invoices);
        // Generate 24 phonebook contacts (mix of suppliers, customers, both, and unassigned)
        const contacts = this.generateContacts(24);
        this.saveContacts(contacts);
        // Generate auction showcase data (10 auctions with bids)
        const auctionData = this.generateAuctionData();
        this.saveAuctions(auctionData.auctions);
        this.saveAuctionBids(auctionData.bids);
        this.saveAuctionSummaries(auctionData.summaries);
        console.log('✅ Mock data seeded with complete coverage:', {
            suppliers: suppliers.length,
            diamonds: diamonds.length,
            purchases: updatedPurchases.length,
            supplierPayments: payments.length,
            customerPayments: customerPayments.length,
            memos: memos.length,
            invoices: invoices.length,
            contacts: contacts.length,
            auctions: auctionData.auctions.length,
            auctionBids: auctionData.bids.length,
            totalRecords: suppliers.length + diamonds.length + updatedPurchases.length + payments.length + customerPayments.length + memos.length + invoices.length + contacts.length + auctionData.auctions.length
        });
    }
    // ============================================
    // Generate Mock Data
    // ============================================
    generateSuppliers(count) {
        const suppliers = [];
        const counters = this.getCounters();
        for (let i = 0; i < count; i++) {
            const companyName = COMPANY_NAMES[i % COMPANY_NAMES.length];
            const firstName = CONTACT_FIRST_NAMES[Math.floor(Math.random() * CONTACT_FIRST_NAMES.length)];
            const lastName = CONTACT_LAST_NAMES[Math.floor(Math.random() * CONTACT_LAST_NAMES.length)];
            const cityIndex = i % CITIES.length;
            const statusOptions = ['active', 'active', 'active', 'active', 'inactive']; // 80% active
            const typeOptions = ['wholesaler', 'wholesaler', 'manufacturer', 'broker'];
            suppliers.push({
                id: `SUP${counters.supplierId.toString().padStart(6, '0')}`,
                type: 'supplier',
                supplierCode: `SUP${(counters.supplierId + 100).toString()}`,
                companyName,
                contactPerson: `${firstName} ${lastName}`,
                email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${companyName.toLowerCase().replace(/\s+/g, '')}.com`,
                phone: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                fax: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                mobilePhone: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                website: `https://www.${companyName.toLowerCase().replace(/\s+/g, '')}.com`,
                address: {
                    street: `${Math.floor(Math.random() * 9000 + 1000)} Diamond Street`,
                    city: CITIES[cityIndex],
                    state: STATES[cityIndex],
                    zipCode: `${Math.floor(Math.random() * 90000 + 10000)}`,
                    country: 'USA',
                },
                supplierType: typeOptions[i % typeOptions.length],
                taxId: `${Math.floor(Math.random() * 90 + 10)}-${Math.floor(Math.random() * 9000000 + 1000000)}`,
                paymentTerms: ['Net 30', 'Net 60', 'COD', 'Net 15'][i % 4],
                totalPurchases: 0,
                totalOwed: 0,
                totalMemoDebt: 0,
                status: statusOptions[i % statusOptions.length],
                verificationStatus: 'verified',
                notes: i % 3 === 0 ? `Preferred supplier - excellent quality and service` : '',
                tags: ['diamond-supplier', i % 2 === 0 ? 'certified' : 'verified'],
                createdAt: new Date(Date.now() - Math.floor(Math.random() * 90) * 24 * 60 * 60 * 1000).toISOString(),
                updatedAt: new Date(Date.now() - Math.floor(Math.random() * 7) * 24 * 60 * 60 * 1000).toISOString(),
                createdBy: 'MOCK_USER',
            });
            counters.supplierId++;
        }
        this.updateCounters(counters);
        return suppliers;
    }
    /**
     * Generate realistic diamond inventory
     * Pattern: Distribution based on market data (shape, color, clarity)
     */
    generateRealisticDiamonds(count) {
        const diamonds = [];
        const counters = this.getCounters();
        const shapes = ['Round', 'Princess', 'Emerald', 'Oval', 'Cushion', 'Radiant', 'Asscher', 'Marquise', 'Heart', 'Pear'];
        const shapeWeights = [45, 15, 10, 10, 8, 5, 3, 2, 1, 1]; // Round is 45%
        const colors = ['D', 'E', 'F', 'G', 'H', 'I', 'J'];
        const clarities = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2'];
        const cuts = ['Excellent', 'Very Good', 'Good'];
        const labs = ['GIA', 'AGS', 'IGI', 'HRD'];
        for (let i = 0; i < count; i++) {
            // Weighted shape selection (Round 45%, others distributed)
            const shapeIndex = this.weightedRandom(shapeWeights);
            const shape = shapes[shapeIndex];
            // Carat: 0.5-3.0ct (weighted toward 1-2ct)
            const carat = parseFloat((Math.random() < 0.7 ? (Math.random() * 1.5 + 0.5) : (Math.random() * 1.5 + 1.5)).toFixed(2));
            // Price per carat: $3,000-$15,000 (varies by quality)
            const pricePerCarat = Math.floor(Math.random() * 12000 + 3000);
            const totalPrice = Math.round(carat * pricePerCarat);
            // Cost per carat (if available): 70-85% of price
            const costPerCarat = Math.random() < 0.5 ? Math.floor(pricePerCarat * (0.70 + Math.random() * 0.15)) : undefined;
            const totalCost = costPerCarat ? Math.round(carat * costPerCarat) : undefined;
            // Import date: spread over last 90 days
            const daysAgo = Math.floor(Math.random() * 90);
            const importedAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
            // 85% have certificates
            const hasCert = Math.random() < 0.85;
            const certificateNumber = hasCert ? `${Math.floor(Math.random() * 9000000 + 1000000)}` : '';
            const stockNumber = `STK${counters.diamondId.toString().padStart(5, '0')}`;
            diamonds.push({
                id: `DIA${counters.diamondId.toString().padStart(6, '0')}`,
                shape,
                shapeCode: shape.substring(0, 2).toUpperCase(),
                caratWeight: carat,
                color: colors[Math.floor(Math.random() * colors.length)],
                clarity: clarities[Math.floor(Math.random() * clarities.length)],
                cut: cuts[Math.floor(Math.random() * cuts.length)],
                polish: cuts[Math.floor(Math.random() * cuts.length)],
                symmetry: cuts[Math.floor(Math.random() * cuts.length)],
                fluorescence: Math.random() < 0.7 ? 'None' : ['Faint', 'Medium', 'Strong'][Math.floor(Math.random() * 3)],
                fluorescenceColor: 'Blue',
                lab: labs[Math.floor(Math.random() * labs.length)],
                pricePerCarat,
                totalPrice,
                priceDisplay: `$${totalPrice.toLocaleString()}`,
                cashPrice: totalPrice,
                measurements: `${(carat * 6.5).toFixed(2)} x ${(carat * 6.5).toFixed(2)} x ${(carat * 4).toFixed(2)} mm`,
                depthPercent: 61 + Math.random() * 3,
                tablePercent: 55 + Math.random() * 7,
                girdleThin: 'Thin',
                girdleThick: 'Slightly Thick',
                girdlePercent: 3.5,
                culetSize: 'None',
                culetCondition: 'Pointed',
                crownHeight: 15,
                crownAngle: 34.5,
                pavilionDepth: 43,
                pavilionAngle: 40.8,
                certificateNumber,
                certificateImageFilename: hasCert ? `cert_${certificateNumber}.jpg` : '',
                imageFilename: `diamond_${stockNumber}.jpg`,
                imageUrl: '',
                stockNumber,
                pairStockNumber: '',
                isMatchedPairSeparable: false,
                fancyColor: '',
                fancyColorIntensity: '',
                fancyColorOvertone: '',
                treatment: 'None',
                reportIssueLocation: 'New York',
                comments: '',
                parcelStoneCount: 1,
                tradeShow: '',
                availability: 'Available',
                rapLink: '',
                city: 'New York',
                state: 'NY',
                country: 'USA',
                milky: 'No',
                shade: 'No',
                eyeClean: 'Yes',
                blackInclusion: 'No',
                centerInclusion: 'No',
                openInclusion: 'No',
                brand: '',
                laserInscription: '',
                memberComments: '',
                status: 'available',
                featured: Math.random() < 0.1,
                certified: hasCert,
                costPerCarat,
                totalCost,
                possessionType: 'owned',
                source: 'mock-data',
                importedAt,
                lastUpdated: importedAt,
            });
            counters.diamondId++;
        }
        this.updateCounters(counters);
        return diamonds;
    }
    weightedRandom(weights) {
        const total = weights.reduce((sum, w) => sum + w, 0);
        let random = Math.random() * total;
        for (let i = 0; i < weights.length; i++) {
            if (random < weights[i])
                return i;
            random -= weights[i];
        }
        return 0;
    }
    // ============================================
    // REALISTIC 2-MONTH BUSINESS SIMULATION
    // ============================================
    /**
     * Generate realistic purchases over 60-day period
     * Pattern: 2-4 purchases per week, concentrated with preferred suppliers
     * Now links to real mock diamonds for complete relational integrity
     */
    generateRealisticPurchases(suppliers, diamonds, days) {
        const purchases = [];
        const counters = this.getCounters();
        const activeSuppliers = suppliers.filter(s => s.status === 'active');
        // Preferred suppliers (Pareto: 80/20 rule - 3 suppliers get 60% of business)
        const preferredSuppliers = activeSuppliers.slice(0, 3);
        const otherSuppliers = activeSuppliers.slice(3);
        // Generate 6-8 purchases per week over 8 weeks = ~50 purchases
        const purchasesPerWeek = [6, 7, 8, 9, 7, 6, 8, 10]; // Slightly increasing trend
        let totalPurchases = 0;
        for (let week = 0; week < 8; week++) {
            const purchaseCount = purchasesPerWeek[week];
            for (let i = 0; i < purchaseCount; i++) {
                // 60% from preferred suppliers, 40% from others
                const supplier = Math.random() < 0.6
                    ? preferredSuppliers[Math.floor(Math.random() * preferredSuppliers.length)]
                    : otherSuppliers[Math.floor(Math.random() * otherSuppliers.length)];
                // Purchase date: spread across the week
                const daysAgo = (7 - week) * 7 + Math.floor(Math.random() * 7);
                const purchaseDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
                // Due date: Net 30 (some suppliers have Net 60 or Net 15)
                let paymentTermDays = 30;
                if (supplier.paymentTerms.includes('60'))
                    paymentTermDays = 60;
                else if (supplier.paymentTerms.includes('15'))
                    paymentTermDays = 15;
                else if (supplier.paymentTerms === 'COD')
                    paymentTermDays = 0;
                const dueDate = new Date(purchaseDate.getTime() + paymentTermDays * 24 * 60 * 60 * 1000);
                // Items: 1-5 diamonds per purchase (weighted toward 1-2)
                const itemCount = Math.random() < 0.6 ? 1 : (Math.random() < 0.8 ? 2 : Math.floor(Math.random() * 3) + 3);
                const items = Array.from({ length: itemCount }, (_, idx) => {
                    const caratWeight = (Math.random() * 2.5 + 0.5).toFixed(2);
                    const costPerCarat = Math.floor(Math.random() * 10000 + 3000);
                    const totalCost = parseFloat(caratWeight) * costPerCarat;
                    const stockNumber = `STK${Math.floor(Math.random() * 90000 + 10000)}`;
                    return {
                        id: `LINE${Date.now()}${idx}${totalPurchases}`,
                        diamondId: `DIA${Math.floor(Math.random() * 900000 + 100000)}`,
                        stockNumber,
                        description: `${caratWeight}ct ${['Round', 'Princess', 'Emerald', 'Oval', 'Cushion'][Math.floor(Math.random() * 5)]} ${['D', 'E', 'F', 'G', 'H'][Math.floor(Math.random() * 5)]} ${['IF', 'VVS1', 'VVS2', 'VS1', 'VS2'][Math.floor(Math.random() * 5)]}`,
                        caratWeight: parseFloat(caratWeight),
                        costPerCarat,
                        quantity: 1,
                        totalCost,
                    };
                });
                const subtotal = items.reduce((sum, item) => sum + item.totalCost, 0);
                const taxAmount = subtotal * 0.085;
                const shippingCost = Math.floor(Math.random() * 100 + 50);
                const total = subtotal + taxAmount + shippingCost;
                // Determine payment status based on age and due date
                const now = new Date();
                const daysSincePurchase = Math.floor((now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
                const daysUntilDue = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                let paymentStatus;
                let amountPaid = 0;
                // Realistic payment pattern:
                // - Purchases > 45 days old: 85% paid
                // - Purchases 30-45 days: 60% paid
                // - Purchases 15-30 days: 30% paid
                // - Purchases < 15 days: 10% paid (mostly unpaid)
                if (daysSincePurchase > 45) {
                    if (Math.random() < 0.85) {
                        paymentStatus = 'paid';
                        amountPaid = total;
                    }
                    else if (Math.random() < 0.5) {
                        paymentStatus = 'partial';
                        amountPaid = Math.floor(total * (Math.random() * 0.4 + 0.4));
                    }
                    else {
                        paymentStatus = 'unpaid';
                    }
                }
                else if (daysSincePurchase > 30) {
                    if (Math.random() < 0.6) {
                        paymentStatus = 'paid';
                        amountPaid = total;
                    }
                    else if (Math.random() < 0.3) {
                        paymentStatus = 'partial';
                        amountPaid = Math.floor(total * (Math.random() * 0.5 + 0.3));
                    }
                    else {
                        paymentStatus = 'unpaid';
                    }
                }
                else if (daysSincePurchase > 15) {
                    if (Math.random() < 0.3) {
                        paymentStatus = 'paid';
                        amountPaid = total;
                    }
                    else if (Math.random() < 0.2) {
                        paymentStatus = 'partial';
                        amountPaid = Math.floor(total * (Math.random() * 0.6 + 0.2));
                    }
                    else {
                        paymentStatus = 'unpaid';
                    }
                }
                else {
                    // Recent purchases - mostly unpaid
                    if (Math.random() < 0.1) {
                        paymentStatus = 'paid';
                        amountPaid = total;
                    }
                    else {
                        paymentStatus = 'unpaid';
                    }
                }
                purchases.push({
                    id: `PUR${counters.purchaseId.toString().padStart(6, '0')}`,
                    type: 'purchase',
                    purchaseNumber: `PO-${counters.purchaseNumber}`,
                    supplierId: supplier.id,
                    supplierCode: supplier.supplierCode,
                    supplierName: supplier.companyName,
                    purchaseDate: purchaseDate.toISOString(),
                    dueDate: dueDate.toISOString(),
                    purchaseType: 'outright',
                    items,
                    subtotal,
                    taxAmount,
                    shippingCost,
                    discountAmount: 0,
                    total,
                    paymentStatus,
                    amountPaid,
                    amountOwed: total - amountPaid,
                    notes: '',
                    createdAt: purchaseDate.toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: 'MOCK_USER',
                });
                counters.purchaseId++;
                counters.purchaseNumber++;
                totalPurchases++;
            }
        }
        this.updateCounters(counters);
        return purchases;
    }
    /**
     * OLD METHOD - Kept for backward compatibility
     */
    generatePurchases(suppliers, count) {
        const purchases = [];
        const counters = this.getCounters();
        const activeSuppliers = suppliers.filter(s => s.status === 'active');
        for (let i = 0; i < count; i++) {
            const supplier = activeSuppliers[i % activeSuppliers.length];
            const itemCount = Math.floor(Math.random() * 3) + 1; // 1-3 items per purchase
            const purchaseDate = new Date(Date.now() - Math.floor(Math.random() * 60) * 24 * 60 * 60 * 1000);
            // Create some OVERDUE purchases (25% chance of being overdue)
            let dueDate;
            if (i < count * 0.25) {
                // Past due date (5-60 days ago)
                dueDate = new Date(Date.now() - Math.floor(Math.random() * 55 + 5) * 24 * 60 * 60 * 1000);
            }
            else {
                // Future due date (normal)
                dueDate = new Date(purchaseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            }
            const items = Array.from({ length: itemCount }, (_, idx) => {
                const caratWeight = (Math.random() * 3 + 0.5).toFixed(2);
                const costPerCarat = Math.floor(Math.random() * 8000 + 2000);
                const totalCost = parseFloat(caratWeight) * costPerCarat;
                const stockNumber = `STK${Math.floor(Math.random() * 90000 + 10000)}`;
                return {
                    id: `LINE${Date.now()}${idx}`,
                    diamondId: `DIA${Math.floor(Math.random() * 900000 + 100000)}`,
                    stockNumber,
                    description: `${caratWeight}ct ${['Round', 'Princess', 'Emerald', 'Oval'][Math.floor(Math.random() * 4)]} ${['D', 'E', 'F', 'G', 'H'][Math.floor(Math.random() * 5)]} ${['IF', 'VVS1', 'VVS2', 'VS1', 'VS2'][Math.floor(Math.random() * 5)]}`,
                    caratWeight: parseFloat(caratWeight),
                    costPerCarat,
                    quantity: 1,
                    totalCost,
                };
            });
            const subtotal = items.reduce((sum, item) => sum + item.totalCost, 0);
            const taxAmount = subtotal * 0.085; // 8.5% tax
            const shippingCost = Math.floor(Math.random() * 100 + 50);
            const total = subtotal + taxAmount + shippingCost;
            // Determine payment status - overdue ones are mostly unpaid
            const now = new Date();
            let paymentStatus;
            let amountPaid = 0;
            if (dueDate < now && Math.random() < 0.8) {
                // Past due - 80% unpaid, 20% partial
                if (Math.random() < 0.8) {
                    paymentStatus = 'unpaid';
                }
                else {
                    paymentStatus = 'partial';
                    amountPaid = Math.floor(total * (Math.random() * 0.4 + 0.1)); // 10-50% paid
                }
            }
            else {
                // Not overdue - normal distribution
                const statusRand = Math.random();
                if (statusRand < 0.4) {
                    paymentStatus = 'unpaid';
                }
                else if (statusRand < 0.7) {
                    paymentStatus = 'partial';
                    amountPaid = Math.floor(total * (Math.random() * 0.5 + 0.3)); // 30-80% paid
                }
                else {
                    paymentStatus = 'paid';
                    amountPaid = total;
                }
            }
            purchases.push({
                id: `PUR${counters.purchaseId.toString().padStart(6, '0')}`,
                type: 'purchase',
                purchaseNumber: `PO-${counters.purchaseNumber}`,
                supplierId: supplier.id,
                supplierCode: supplier.supplierCode,
                supplierName: supplier.companyName,
                purchaseDate: purchaseDate.toISOString(),
                dueDate: dueDate.toISOString(),
                purchaseType: ['outright', 'memo', 'consignment'][i % 3],
                items,
                subtotal,
                taxAmount,
                shippingCost,
                discountAmount: 0,
                total,
                paymentStatus,
                amountPaid,
                amountOwed: total - amountPaid,
                notes: i % 5 === 0 ? 'Special order - high priority' : '',
                createdAt: purchaseDate.toISOString(),
                updatedAt: new Date(purchaseDate.getTime() + Math.floor(Math.random() * 7) * 24 * 60 * 60 * 1000).toISOString(),
                createdBy: 'MOCK_USER',
            });
            counters.purchaseId++;
            counters.purchaseNumber++;
        }
        this.updateCounters(counters);
        return purchases;
    }
    /**
     * Generate realistic payments based on purchase timing
     * Pattern: Payments made 30-40 days after purchase (some early, some late)
     */
    generateRealisticPayments(purchases, suppliers) {
        const payments = [];
        const counters = this.getCounters();
        // Get all paid or partial purchases
        const paidPurchases = purchases.filter(p => p.paymentStatus !== 'unpaid');
        paidPurchases.forEach(purchase => {
            const supplier = suppliers.find(s => s.id === purchase.supplierId);
            if (!supplier)
                return;
            // Payment timing: avg 35 days after purchase (range: 15-50 days)
            const daysAfterPurchase = Math.floor(Math.random() * 35 + 15);
            const paymentDate = new Date(new Date(purchase.purchaseDate).getTime() + daysAfterPurchase * 24 * 60 * 60 * 1000);
            // Don't create future payments
            if (paymentDate > new Date())
                return;
            const amount = purchase.paymentStatus === 'paid' ? purchase.total : purchase.amountPaid;
            const paidInFull = purchase.paymentStatus === 'paid';
            payments.push({
                id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
                type: 'supplier-payment',
                paymentNumber: `SPAY-${counters.paymentId}`,
                supplierId: supplier.id,
                supplierCode: supplier.supplierCode,
                supplierName: supplier.companyName,
                paymentFor: 'purchase',
                referenceType: 'purchase',
                referenceId: purchase.id,
                referenceNumber: purchase.purchaseNumber,
                amount,
                paymentDate: paymentDate.toISOString(),
                paymentMethod: ['wire_transfer', 'wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 4)],
                paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                status: 'cleared',
                paidInFull,
                notes: paidInFull ? '' : 'Partial payment',
                createdAt: paymentDate.toISOString(),
                updatedAt: paymentDate.toISOString(),
                createdBy: 'MOCK_USER',
            });
            counters.paymentId++;
        });
        this.updateCounters(counters);
        return payments;
    }
    /**
     * FIX #3: Add instant payments for COD purchases
     */
    addCODAutoPayments(purchases, suppliers) {
        const counters = this.getCounters();
        return purchases.map(purchase => {
            const supplier = suppliers.find(s => s.id === purchase.supplierId);
            if (supplier && supplier.paymentTerms === 'COD') {
                // COD = Cash on Delivery, paid immediately
                return {
                    ...purchase,
                    paymentStatus: 'paid',
                    amountPaid: purchase.total,
                    amountOwed: 0
                };
            }
            return purchase;
        });
    }
    /**
     * FIX #2: Generate payments with some purchases having multiple partial payments
     */
    generateRealisticPaymentsWithPartials(purchases, suppliers) {
        const payments = [];
        const counters = this.getCounters();
        const paidPurchases = purchases.filter(p => p.paymentStatus !== 'unpaid');
        paidPurchases.forEach(purchase => {
            const supplier = suppliers.find(s => s.id === purchase.supplierId);
            if (!supplier)
                return;
            // Skip COD purchases (already have instant payment created separately if needed)
            if (supplier.paymentTerms === 'COD') {
                // Create a single COD payment on purchase date
                const paymentDate = new Date(purchase.purchaseDate);
                if (paymentDate <= new Date()) {
                    payments.push({
                        id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
                        type: 'supplier-payment',
                        paymentNumber: `SPAY-${counters.paymentId}`,
                        supplierId: supplier.id,
                        supplierCode: supplier.supplierCode,
                        supplierName: supplier.companyName,
                        paymentFor: 'purchase',
                        referenceType: 'purchase',
                        referenceId: purchase.id,
                        referenceNumber: purchase.purchaseNumber,
                        amount: purchase.total,
                        paymentDate: paymentDate.toISOString(),
                        paymentMethod: 'wire_transfer',
                        paymentReference: `COD-${Math.floor(Math.random() * 900000 + 100000)}`,
                        status: 'cleared',
                        paidInFull: true,
                        notes: 'Cash on delivery - paid immediately',
                        createdAt: paymentDate.toISOString(),
                        updatedAt: paymentDate.toISOString(),
                        createdBy: 'MOCK_USER',
                    });
                    counters.paymentId++;
                }
                return;
            }
            // For partial payments: 30% chance of having 2-3 payments instead of 1
            if (purchase.paymentStatus === 'partial' && Math.random() < 0.3) {
                // Create 2-3 partial payments
                const numPayments = Math.random() < 0.5 ? 2 : 3;
                const totalPaid = purchase.amountPaid;
                const paymentAmounts = this.splitAmountIntoPartials(totalPaid, numPayments);
                paymentAmounts.forEach((amount, idx) => {
                    const daysAfter = 15 + (idx * 10) + Math.floor(Math.random() * 5);
                    const paymentDate = new Date(new Date(purchase.purchaseDate).getTime() + daysAfter * 24 * 60 * 60 * 1000);
                    if (paymentDate > new Date())
                        return;
                    payments.push({
                        id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
                        type: 'supplier-payment',
                        paymentNumber: `SPAY-${counters.paymentId}`,
                        supplierId: supplier.id,
                        supplierCode: supplier.supplierCode,
                        supplierName: supplier.companyName,
                        paymentFor: 'purchase',
                        referenceType: 'purchase',
                        referenceId: purchase.id,
                        referenceNumber: purchase.purchaseNumber,
                        amount,
                        paymentDate: paymentDate.toISOString(),
                        paymentMethod: ['wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 3)],
                        paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                        status: 'cleared',
                        paidInFull: idx === paymentAmounts.length - 1 && totalPaid >= purchase.total,
                        notes: idx === 0 ? 'First partial payment' : (idx === paymentAmounts.length - 1 ? 'Final payment' : `Partial payment ${idx + 1}`),
                        createdAt: paymentDate.toISOString(),
                        updatedAt: paymentDate.toISOString(),
                        createdBy: 'MOCK_USER',
                    });
                    counters.paymentId++;
                });
            }
            else {
                // Single payment (normal)
                const daysAfterPurchase = Math.floor(Math.random() * 35 + 15);
                const paymentDate = new Date(new Date(purchase.purchaseDate).getTime() + daysAfterPurchase * 24 * 60 * 60 * 1000);
                if (paymentDate > new Date())
                    return;
                const amount = purchase.paymentStatus === 'paid' ? purchase.total : purchase.amountPaid;
                const paidInFull = purchase.paymentStatus === 'paid';
                payments.push({
                    id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
                    type: 'supplier-payment',
                    paymentNumber: `SPAY-${counters.paymentId}`,
                    supplierId: supplier.id,
                    supplierCode: supplier.supplierCode,
                    supplierName: supplier.companyName,
                    paymentFor: 'purchase',
                    referenceType: 'purchase',
                    referenceId: purchase.id,
                    referenceNumber: purchase.purchaseNumber,
                    amount,
                    paymentDate: paymentDate.toISOString(),
                    paymentMethod: ['wire_transfer', 'wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 4)],
                    paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                    status: 'cleared',
                    paidInFull,
                    notes: paidInFull ? '' : 'Partial payment',
                    createdAt: paymentDate.toISOString(),
                    updatedAt: paymentDate.toISOString(),
                    createdBy: 'MOCK_USER',
                });
                counters.paymentId++;
            }
        });
        this.updateCounters(counters);
        return payments;
    }
    /**
     * Helper: Split amount into multiple partial payments
     */
    splitAmountIntoPartials(total, count) {
        const amounts = [];
        let remaining = total;
        for (let i = 0; i < count - 1; i++) {
            const percent = 0.2 + Math.random() * 0.3; // 20-50% per payment
            const amount = Math.floor(remaining * percent);
            amounts.push(amount);
            remaining -= amount;
        }
        amounts.push(remaining); // Last payment gets remainder
        return amounts;
    }
    /**
     * FIX #1: Generate customer payments for paid invoices
     */
    generateCustomerPayments(invoices) {
        const payments = [];
        const counters = this.getCounters();
        const paidInvoices = invoices.filter(inv => inv.status === InvoiceStatus.PAID);
        paidInvoices.forEach(invoice => {
            // Some invoices get multiple partial payments (20% chance)
            if (Math.random() < 0.2 && invoice.total > 10000) {
                // Create 2 partial payments
                const firstAmount = Math.floor(invoice.total * (0.4 + Math.random() * 0.3)); // 40-70%
                const secondAmount = invoice.total - firstAmount;
                // First payment
                const firstPaymentDate = new Date(new Date(invoice.issueDate).getTime() + Math.floor(Math.random() * 20 + 10) * 24 * 60 * 60 * 1000);
                if (firstPaymentDate <= new Date()) {
                    payments.push({
                        id: `CPAY${counters.customerPaymentId.toString().padStart(6, '0')}`,
                        type: 'customer-payment',
                        paymentNumber: `CPAY-${counters.customerPaymentId}`,
                        customerId: invoice.customerId,
                        customerName: invoice.customerName,
                        customerBusinessName: invoice.customerBusinessName,
                        invoiceId: invoice.id,
                        invoiceNumber: invoice.invoiceNumber,
                        amount: firstAmount,
                        paymentDate: firstPaymentDate.toISOString(),
                        paymentMethod: ['wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 3)],
                        paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                        status: 'cleared',
                        notes: 'First partial payment',
                        createdAt: firstPaymentDate.toISOString(),
                        updatedAt: firstPaymentDate.toISOString(),
                        createdBy: 'MOCK_USER',
                    });
                    counters.customerPaymentId++;
                }
                // Second payment
                const secondPaymentDate = new Date(firstPaymentDate.getTime() + Math.floor(Math.random() * 15 + 7) * 24 * 60 * 60 * 1000);
                if (secondPaymentDate <= new Date()) {
                    payments.push({
                        id: `CPAY${counters.customerPaymentId.toString().padStart(6, '0')}`,
                        type: 'customer-payment',
                        paymentNumber: `CPAY-${counters.customerPaymentId}`,
                        customerId: invoice.customerId,
                        customerName: invoice.customerName,
                        customerBusinessName: invoice.customerBusinessName,
                        invoiceId: invoice.id,
                        invoiceNumber: invoice.invoiceNumber,
                        amount: secondAmount,
                        paymentDate: secondPaymentDate.toISOString(),
                        paymentMethod: ['wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 3)],
                        paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                        status: 'cleared',
                        notes: 'Final payment - paid in full',
                        createdAt: secondPaymentDate.toISOString(),
                        updatedAt: secondPaymentDate.toISOString(),
                        createdBy: 'MOCK_USER',
                    });
                    counters.customerPaymentId++;
                }
            }
            else {
                // Single payment (normal)
                const paymentDate = new Date(new Date(invoice.issueDate).getTime() + Math.floor(Math.random() * 30 + 15) * 24 * 60 * 60 * 1000);
                if (paymentDate <= new Date() && invoice.paidDate) {
                    payments.push({
                        id: `CPAY${counters.customerPaymentId.toString().padStart(6, '0')}`,
                        type: 'customer-payment',
                        paymentNumber: `CPAY-${counters.customerPaymentId}`,
                        customerId: invoice.customerId,
                        customerName: invoice.customerName,
                        customerBusinessName: invoice.customerBusinessName,
                        invoiceId: invoice.id,
                        invoiceNumber: invoice.invoiceNumber,
                        amount: invoice.total,
                        paymentDate: invoice.paidDate,
                        paymentMethod: ['wire_transfer', 'wire_transfer', 'check', 'ach'][Math.floor(Math.random() * 4)],
                        paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                        status: 'cleared',
                        notes: '',
                        createdAt: invoice.paidDate,
                        updatedAt: invoice.paidDate,
                        createdBy: 'MOCK_USER',
                    });
                    counters.customerPaymentId++;
                }
            }
        });
        this.updateCounters(counters);
        return payments;
    }
    /**
     * FIX #4: Add edge cases for testing
     */
    addEdgeCases(purchases, invoices) {
        const counters = this.getCounters();
        const customerPayments = this.getCustomerPayments();
        // Edge Case 1: Create 1 bounced customer payment (check)
        const paidInvoice = invoices.find(inv => inv.status === InvoiceStatus.PAID);
        if (paidInvoice && customerPayments.length > 0) {
            customerPayments.push({
                id: `CPAY${counters.customerPaymentId.toString().padStart(6, '0')}`,
                type: 'customer-payment',
                paymentNumber: `CPAY-${counters.customerPaymentId}`,
                customerId: paidInvoice.customerId,
                customerName: paidInvoice.customerName,
                customerBusinessName: paidInvoice.customerBusinessName,
                invoiceId: paidInvoice.id,
                invoiceNumber: paidInvoice.invoiceNumber,
                amount: 5000,
                paymentDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                paymentMethod: 'check',
                paymentReference: `CHK-BOUNCED-${Math.floor(Math.random() * 9000 + 1000)}`,
                status: 'bounced',
                bouncedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
                notes: 'Check bounced - insufficient funds. Need to contact customer.',
                createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
                createdBy: 'MOCK_USER',
            });
            counters.customerPaymentId++;
            this.saveCustomerPayments(customerPayments);
        }
        // Edge Case 2: Mark 1 invoice as cancelled
        if (invoices.length > 0) {
            const cancelledInvoice = invoices.find(inv => inv.status === InvoiceStatus.ISSUED);
            if (cancelledInvoice) {
                this.updateInvoice(cancelledInvoice.id, {
                    status: InvoiceStatus.CANCELLED,
                    notes: 'Cancelled by customer request'
                });
            }
        }
        this.updateCounters(counters);
    }
    /**
     * OLD METHOD - Kept for backward compatibility
     */
    generatePayments(purchases, suppliers, count) {
        const payments = [];
        const counters = this.getCounters();
        const paidOrPartialPurchases = purchases.filter(p => p.paymentStatus !== 'unpaid');
        for (let i = 0; i < Math.min(count, paidOrPartialPurchases.length); i++) {
            const purchase = paidOrPartialPurchases[i];
            const supplier = suppliers.find(s => s.id === purchase.supplierId);
            if (!supplier)
                continue;
            const paymentDate = new Date(new Date(purchase.purchaseDate).getTime() + Math.floor(Math.random() * 20 + 5) * 24 * 60 * 60 * 1000);
            const amount = purchase.paymentStatus === 'paid' ? purchase.total : purchase.amountPaid;
            const paidInFull = purchase.paymentStatus === 'paid';
            payments.push({
                id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
                type: 'supplier-payment',
                paymentNumber: `SPAY-${counters.paymentId}`,
                supplierId: supplier.id,
                supplierCode: supplier.supplierCode,
                supplierName: supplier.companyName,
                paymentFor: 'purchase',
                referenceType: 'purchase',
                referenceId: purchase.id,
                referenceNumber: purchase.purchaseNumber,
                amount,
                paymentDate: paymentDate.toISOString(),
                paymentMethod: ['check', 'wire_transfer', 'ach', 'credit_card'][i % 4],
                paymentReference: `REF${Math.floor(Math.random() * 900000 + 100000)}`,
                status: 'cleared',
                paidInFull,
                notes: paidInFull ? 'Paid in full - all items transferred to owned status' : 'Partial payment received',
                createdAt: paymentDate.toISOString(),
                updatedAt: paymentDate.toISOString(),
                createdBy: 'MOCK_USER',
            });
            counters.paymentId++;
        }
        this.updateCounters(counters);
        return payments;
    }
    /**
     * Generate realistic memos over 60-day period
     * Pattern: 2-3 memos per week, 80% approval rate, varied statuses
     */
    generateRealisticMemos(days) {
        const memos = [];
        const counters = this.getCounters();
        // Repeat dealers (Pareto: 5 dealers make 70% of requests)
        const dealerNames = ['Alex Thompson', 'Maria Garcia', 'James Chen', 'Lisa Anderson', 'Robert Martinez', 'Sarah Williams', 'David Brown', 'Emma Davis'];
        const dealerBusinesses = ['Thompson Jewelers', 'Garcia Diamonds', 'Chen Fine Jewelry', 'Anderson Gems', 'Martinez Diamond Gallery', 'Williams Luxury', 'Brown & Co', 'Davis Fine Gems'];
        // VIP dealers (make more requests)
        const vipDealerIndices = [0, 1, 2]; // First 3 are VIPs
        const clientNames = ['Mr. Johnson', 'Mrs. Williams', 'Dr. Brown', 'Ms. Davis', 'Mr. Taylor', 'Mrs. Anderson', 'Dr. Martinez', 'Ms. Chen', 'Mr. Thompson', 'Mrs. Garcia'];
        const shapes = ['Round', 'Princess', 'Emerald', 'Oval', 'Cushion', 'Radiant', 'Asscher'];
        const colors = ['D', 'E', 'F', 'G', 'H', 'I'];
        const clarities = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1'];
        const cuts = ['Excellent', 'Very Good', 'Good'];
        const labs = ['GIA', 'AGS', 'IGI', 'GIA', 'GIA']; // 60% GIA
        // Generate 3-5 memos per week over 8 weeks = ~30 memos
        const memosPerWeek = [3, 4, 3, 5, 4, 3, 4, 5]; // Slightly increasing
        for (let week = 0; week < 8; week++) {
            const memoCount = memosPerWeek[week];
            for (let i = 0; i < memoCount; i++) {
                // 70% from VIP dealers, 30% from others
                const dealerIndex = Math.random() < 0.7
                    ? vipDealerIndices[Math.floor(Math.random() * vipDealerIndices.length)]
                    : Math.floor(Math.random() * dealerNames.length);
                // Request date: spread across the week
                const daysAgo = (7 - week) * 7 + Math.floor(Math.random() * 7);
                const requestedDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
                // Status distribution (realistic workflow):
                // - 80% approved path (approved, shipped, with_client, sold, returned)
                // - 15% pending
                // - 5% rejected
                const statusRand = Math.random();
                let status;
                let approvedAt;
                let shippedAt;
                let dueDate;
                if (statusRand < 0.25) {
                    // 25% - Shipped (in transit)
                    status = MemoStatus.SHIPPED;
                    approvedAt = new Date(requestedDate.getTime() + 24 * 60 * 60 * 1000).toISOString();
                    shippedAt = new Date(requestedDate.getTime() + 48 * 60 * 60 * 1000).toISOString();
                    const dueDateTimestamp = new Date(shippedAt).getTime() + (7 + Math.floor(Math.random() * 7)) * 24 * 60 * 60 * 1000;
                    dueDate = new Date(dueDateTimestamp).toISOString();
                }
                else if (statusRand < 0.45) {
                    // 20% - With client (dealer showing to customer)
                    status = MemoStatus.WITH_CLIENT;
                    approvedAt = new Date(requestedDate.getTime() + 24 * 60 * 60 * 1000).toISOString();
                    shippedAt = new Date(requestedDate.getTime() + 48 * 60 * 60 * 1000).toISOString();
                    const dueDateTimestamp = new Date(shippedAt).getTime() + (3 + Math.floor(Math.random() * 10)) * 24 * 60 * 60 * 1000;
                    dueDate = new Date(dueDateTimestamp).toISOString();
                }
                else if (statusRand < 0.60) {
                    // 15% - Approved (ready to ship)
                    status = MemoStatus.APPROVED;
                    approvedAt = new Date(requestedDate.getTime() + 24 * 60 * 60 * 1000).toISOString();
                    dueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
                }
                else if (statusRand < 0.75) {
                    // 15% - Pending (awaiting approval)
                    status = MemoStatus.PENDING;
                }
                else if (statusRand < 0.85) {
                    // 10% - Sold (memo converted to sale)
                    status = MemoStatus.SOLD;
                    approvedAt = new Date(requestedDate.getTime() + 24 * 60 * 60 * 1000).toISOString();
                    shippedAt = new Date(requestedDate.getTime() + 48 * 60 * 60 * 1000).toISOString();
                }
                else if (statusRand < 0.95) {
                    // 10% - Returned
                    status = MemoStatus.RETURNED;
                    approvedAt = new Date(requestedDate.getTime() + 24 * 60 * 60 * 1000).toISOString();
                    shippedAt = new Date(requestedDate.getTime() + 48 * 60 * 60 * 1000).toISOString();
                }
                else {
                    // 5% - Rejected
                    status = MemoStatus.REJECTED;
                }
                // Diamond details - realistic distribution
                const caratWeight = (Math.random() * 2.5 + 0.5).toFixed(2);
                const pricePerCarat = Math.floor(Math.random() * 15000 + 5000);
                const totalPrice = parseFloat(caratWeight) * pricePerCarat;
                // 90% of memos have certificates (high-end market)
                const hasCertificate = Math.random() < 0.9;
                const certificateNumber = hasCertificate ? `${Math.floor(Math.random() * 9000000 + 1000000)}` : undefined;
                memos.push({
                    id: `MEMO${counters.memoId.toString().padStart(6, '0')}`,
                    dealerId: `DEALER${dealerIndex + 1}`,
                    dealerEmail: `${dealerNames[dealerIndex].toLowerCase().replace(' ', '.')}@${dealerBusinesses[dealerIndex].toLowerCase().replace(/\s+/g, '')}.com`,
                    dealerName: dealerNames[dealerIndex],
                    dealerBusinessName: dealerBusinesses[dealerIndex],
                    diamondId: `DIA${Math.floor(Math.random() * 900000 + 100000)}`,
                    diamondDetails: {
                        shape: shapes[Math.floor(Math.random() * shapes.length)],
                        caratWeight: parseFloat(caratWeight),
                        color: colors[Math.floor(Math.random() * colors.length)],
                        clarity: clarities[Math.floor(Math.random() * clarities.length)],
                        cut: cuts[Math.floor(Math.random() * cuts.length)],
                        lab: labs[Math.floor(Math.random() * labs.length)],
                        certificateNumber,
                        stockNumber: `STK${Math.floor(Math.random() * 90000 + 10000)}`,
                        priceUSD: totalPrice,
                    },
                    status,
                    requestedAt: requestedDate.toISOString(),
                    approvedAt,
                    shippedAt,
                    dueDate,
                    clientName: statusRand < 0.75 ? clientNames[Math.floor(Math.random() * clientNames.length)] : undefined,
                    clientContact: statusRand < 0.75 ? `${clientNames[Math.floor(Math.random() * clientNames.length)].toLowerCase().replace(/\s+/g, '')}@example.com` : undefined,
                    purpose: ['viewing', 'appraisal', 'consideration'][Math.floor(Math.random() * 3)],
                    dealerNotes: Math.random() < 0.3 ? 'Client is very interested' : '',
                    history: [],
                    createdAt: requestedDate.toISOString(),
                    updatedAt: new Date().toISOString(),
                });
                counters.memoId++;
            }
        }
        this.updateCounters(counters);
        return memos;
    }
    /**
     * OLD METHOD - Kept for backward compatibility
     */
    generateMemos(count) {
        const memos = [];
        const counters = this.getCounters();
        const dealerNames = ['Alex Thompson', 'Maria Garcia', 'James Chen', 'Lisa Anderson', 'Robert Martinez'];
        const dealerBusinesses = ['Thompson Jewelers', 'Garcia Diamonds', 'Chen Fine Jewelry', 'Anderson Gems', 'Martinez Diamond Gallery'];
        const clientNames = ['Mr. Johnson', 'Mrs. Williams', 'Dr. Brown', 'Ms. Davis', 'Mr. Taylor'];
        const shapes = ['Round', 'Princess', 'Emerald', 'Oval', 'Cushion'];
        const colors = ['D', 'E', 'F', 'G', 'H'];
        const clarities = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2'];
        const cuts = ['Excellent', 'Very Good', 'Good'];
        const labs = ['GIA', 'AGS', 'IGI'];
        for (let i = 0; i < count; i++) {
            const dealerIndex = i % dealerNames.length;
            const requestedDate = new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000);
            // 60% approved/shipped/with_client, 20% pending, 10% returned, 10% other
            const statusRand = Math.random();
            let status;
            let approvedAt;
            let shippedAt;
            let dueDate;
            if (statusRand < 0.3) {
                status = MemoStatus.SHIPPED;
                approvedAt = new Date(requestedDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
                shippedAt = new Date(requestedDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString();
                dueDate = new Date(Date.now() + Math.floor(Math.random() * 14 - 7) * 24 * 60 * 60 * 1000).toISOString(); // Some overdue, some not
            }
            else if (statusRand < 0.6) {
                status = MemoStatus.WITH_CLIENT;
                approvedAt = new Date(requestedDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
                shippedAt = new Date(requestedDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString();
                dueDate = new Date(Date.now() + Math.floor(Math.random() * 14 - 7) * 24 * 60 * 60 * 1000).toISOString(); // Some overdue, some not
            }
            else if (statusRand < 0.7) {
                status = MemoStatus.APPROVED;
                approvedAt = new Date(requestedDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
                dueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
            }
            else if (statusRand < 0.8) {
                status = MemoStatus.PENDING;
            }
            else if (statusRand < 0.9) {
                status = MemoStatus.RETURNED;
                approvedAt = new Date(requestedDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
                shippedAt = new Date(requestedDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString();
            }
            else {
                status = MemoStatus.SOLD;
                approvedAt = new Date(requestedDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
                shippedAt = new Date(requestedDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString();
            }
            const caratWeight = (Math.random() * 2 + 0.5).toFixed(2);
            const pricePerCarat = Math.floor(Math.random() * 12000 + 5000);
            const totalPrice = parseFloat(caratWeight) * pricePerCarat;
            // 80% of memos have certificates (for unreturned certificates report)
            const hasCertificate = Math.random() < 0.8;
            const certificateNumber = hasCertificate ? `${Math.floor(Math.random() * 9000000 + 1000000)}` : undefined;
            memos.push({
                id: `MEMO${counters.memoId.toString().padStart(6, '0')}`,
                dealerId: `DEALER${dealerIndex + 1}`,
                dealerEmail: `${dealerNames[dealerIndex].toLowerCase().replace(' ', '.')}@${dealerBusinesses[dealerIndex].toLowerCase().replace(/\s+/g, '')}.com`,
                dealerName: dealerNames[dealerIndex],
                dealerBusinessName: dealerBusinesses[dealerIndex],
                diamondId: `DIA${Math.floor(Math.random() * 900000 + 100000)}`,
                diamondDetails: {
                    shape: shapes[i % shapes.length],
                    caratWeight: parseFloat(caratWeight),
                    color: colors[Math.floor(Math.random() * colors.length)],
                    clarity: clarities[Math.floor(Math.random() * clarities.length)],
                    cut: cuts[Math.floor(Math.random() * cuts.length)],
                    lab: labs[Math.floor(Math.random() * labs.length)],
                    certificateNumber,
                    stockNumber: `STK${Math.floor(Math.random() * 90000 + 10000)}`,
                    priceUSD: totalPrice,
                },
                status,
                requestedAt: requestedDate.toISOString(),
                approvedAt,
                shippedAt,
                dueDate,
                clientName: statusRand < 0.6 ? clientNames[i % clientNames.length] : undefined,
                clientContact: statusRand < 0.6 ? `${clientNames[i % clientNames.length].toLowerCase().replace(/\s+/g, '')}@example.com` : undefined,
                purpose: ['viewing', 'appraisal', 'consideration'][i % 3],
                dealerNotes: i % 3 === 0 ? 'Client is very interested' : '',
                history: [],
                createdAt: requestedDate.toISOString(),
                updatedAt: new Date().toISOString(),
            });
            counters.memoId++;
        }
        this.updateCounters(counters);
        return memos;
    }
    /**
     * Generate realistic invoices from sold memos
     * Pattern: Only sold/approved memos become invoices, 60% conversion rate
     */
    generateRealisticInvoices(memos) {
        const invoices = [];
        const counters = this.getCounters();
        // Only create invoices from approved or sold memos
        const convertibleMemos = memos.filter(m => m.status === MemoStatus.APPROVED || m.status === MemoStatus.SOLD);
        // 60% of approved memos convert to invoices (realistic conversion rate)
        const memosToConvert = convertibleMemos.filter(() => Math.random() < 0.6);
        memosToConvert.forEach(memo => {
            const issueDate = memo.approvedAt ? new Date(memo.approvedAt) : new Date();
            // Due date: Net 30 from issue date
            const dueDate = new Date(issueDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            // Line item from memo
            const lineItem = {
                id: `INVLINE${Date.now()}${counters.invoiceId}`,
                stockNumber: memo.diamondDetails.stockNumber || `STK${Math.floor(Math.random() * 90000 + 10000)}`,
                diamondId: memo.diamondId,
                certificateNumber: memo.diamondDetails.certificateNumber,
                description: `${memo.diamondDetails.caratWeight}ct ${memo.diamondDetails.shape} ${memo.diamondDetails.color} ${memo.diamondDetails.clarity}\n${memo.diamondDetails.cut} Cut, ${memo.diamondDetails.lab} Certified`,
                quantity: 1,
                unitPrice: memo.diamondDetails.priceUSD,
                total: memo.diamondDetails.priceUSD,
            };
            const subtotal = lineItem.total;
            const taxRate = 0.0875;
            const taxAmount = subtotal * taxRate;
            const total = subtotal + taxAmount;
            // Payment status based on invoice age
            const now = new Date();
            const daysSinceIssue = Math.floor((now.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24));
            const daysUntilDue = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            let status;
            let paidDate;
            let amountPaid = 0;
            // Realistic payment pattern for invoices:
            // - Invoices > 45 days old: 70% paid
            // - Invoices 30-45 days: 40% paid
            // - Invoices 15-30 days: 20% paid
            // - Invoices < 15 days: 5% paid
            if (daysSinceIssue > 45) {
                if (Math.random() < 0.7) {
                    status = InvoiceStatus.PAID;
                    paidDate = new Date(issueDate.getTime() + Math.floor(Math.random() * 35 + 15) * 24 * 60 * 60 * 1000).toISOString();
                    amountPaid = total;
                }
                else if (daysUntilDue < 0) {
                    status = InvoiceStatus.OVERDUE;
                    if (Math.random() < 0.3) {
                        amountPaid = Math.floor(total * (Math.random() * 0.5 + 0.2));
                    }
                }
                else {
                    status = InvoiceStatus.ISSUED;
                }
            }
            else if (daysSinceIssue > 30) {
                if (Math.random() < 0.4) {
                    status = InvoiceStatus.PAID;
                    paidDate = new Date(issueDate.getTime() + Math.floor(Math.random() * 25 + 10) * 24 * 60 * 60 * 1000).toISOString();
                    amountPaid = total;
                }
                else if (daysUntilDue < 0) {
                    status = InvoiceStatus.OVERDUE;
                }
                else {
                    status = InvoiceStatus.ISSUED;
                }
            }
            else if (daysSinceIssue > 15) {
                if (Math.random() < 0.2) {
                    status = InvoiceStatus.PAID;
                    paidDate = new Date(issueDate.getTime() + Math.floor(Math.random() * 20 + 5) * 24 * 60 * 60 * 1000).toISOString();
                    amountPaid = total;
                }
                else {
                    status = daysUntilDue < 0 ? InvoiceStatus.OVERDUE : InvoiceStatus.ISSUED;
                }
            }
            else {
                // Recent - mostly issued
                status = Math.random() < 0.05 ? InvoiceStatus.PAID : InvoiceStatus.ISSUED;
                if (status === InvoiceStatus.PAID) {
                    paidDate = new Date(issueDate.getTime() + Math.floor(Math.random() * 10) * 24 * 60 * 60 * 1000).toISOString();
                    amountPaid = total;
                }
            }
            invoices.push({
                id: `INV${counters.invoiceId.toString().padStart(6, '0')}`,
                invoiceNumber: `INV-2025-${counters.invoiceNumber.toString().padStart(3, '0')}`,
                userId: memo.dealerId,
                memoId: memo.id,
                memoApprovedAt: memo.approvedAt || issueDate.toISOString(),
                customerId: memo.dealerId,
                customerEmail: memo.dealerEmail,
                customerName: memo.dealerName,
                customerBusinessName: memo.dealerBusinessName,
                customerAddress: `${Math.floor(Math.random() * 9000 + 1000)} Main St, ${CITIES[counters.invoiceId % CITIES.length]}, ${STATES[counters.invoiceId % STATES.length]} ${Math.floor(Math.random() * 90000 + 10000)}`,
                customerPhone: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                status,
                issueDate: issueDate.toISOString(),
                dueDate: dueDate.toISOString(),
                paidDate,
                lineItems: [lineItem],
                subtotal,
                taxRate,
                taxAmount,
                total,
                amountPaid,
                amountDue: total - amountPaid,
                paymentTerms: 'Net 30',
                companyName: 'Royal Gems, Inc.',
                companyAddress: '123 Diamond Plaza, New York, NY 10001',
                companyPhone: '+1-800-ROYALGEM',
                companyEmail: 'billing@royalgems.com',
                companyTaxId: '12-3456789',
                notes: status === InvoiceStatus.PAID ? 'Thank you for your business!' : '',
                createdAt: issueDate.toISOString(),
                updatedAt: paidDate || issueDate.toISOString(),
                createdBy: 'MOCK_USER',
                history: [],
            });
            counters.invoiceId++;
            counters.invoiceNumber++;
        });
        this.updateCounters(counters);
        return invoices;
    }
    /**
     * OLD METHOD - Kept for backward compatibility
     */
    generateInvoices(memos, count) {
        const invoices = [];
        const counters = this.getCounters();
        // Only create invoices from approved/sold memos
        const approvedMemos = memos.filter(m => m.status === MemoStatus.APPROVED || m.status === MemoStatus.SOLD);
        // Limit to available memos
        const actualCount = Math.min(count, approvedMemos.length);
        for (let i = 0; i < actualCount; i++) {
            const memo = approvedMemos[i];
            const issueDate = memo.approvedAt ? new Date(memo.approvedAt) : new Date(Date.now() - Math.floor(Math.random() * 45) * 24 * 60 * 60 * 1000);
            // Create some OVERDUE invoices (30% chance of being overdue)
            let dueDate;
            if (i < actualCount * 0.3) {
                // Past due date (5-90 days ago)
                dueDate = new Date(Date.now() - Math.floor(Math.random() * 85 + 5) * 24 * 60 * 60 * 1000);
            }
            else {
                // Future due date (normal)
                dueDate = new Date(issueDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            }
            // Create line item from memo diamond details
            const lineItem = {
                id: `INVLINE${Date.now()}`,
                stockNumber: memo.diamondDetails.stockNumber || `STK${Math.floor(Math.random() * 90000 + 10000)}`,
                diamondId: memo.diamondId,
                certificateNumber: memo.diamondDetails.certificateNumber,
                description: `${memo.diamondDetails.caratWeight}ct ${memo.diamondDetails.shape} ${memo.diamondDetails.color} ${memo.diamondDetails.clarity}\n${memo.diamondDetails.cut} Cut, ${memo.diamondDetails.lab} Certified`,
                quantity: 1,
                unitPrice: memo.diamondDetails.priceUSD,
                total: memo.diamondDetails.priceUSD,
            };
            const lineItems = [lineItem];
            const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
            const taxRate = 0.0875; // 8.75%
            const taxAmount = subtotal * taxRate;
            const total = subtotal + taxAmount;
            // Determine status based on due date
            let status;
            let paidDate;
            let amountPaid = 0;
            const now = new Date();
            // If due date is in the past, 70% chance of being overdue (unpaid)
            if (dueDate < now && Math.random() < 0.7) {
                status = InvoiceStatus.OVERDUE;
                // Sometimes partial payment
                if (Math.random() < 0.3) {
                    amountPaid = Math.floor(total * (Math.random() * 0.5 + 0.2)); // 20-70% paid
                }
            }
            else if (Math.random() < 0.5) {
                // 50% of non-overdue are paid
                status = InvoiceStatus.PAID;
                paidDate = new Date(issueDate.getTime() + Math.floor(Math.random() * 25) * 24 * 60 * 60 * 1000).toISOString();
                amountPaid = total;
            }
            else {
                // Rest are issued (awaiting payment)
                status = InvoiceStatus.ISSUED;
            }
            invoices.push({
                id: `INV${counters.invoiceId.toString().padStart(6, '0')}`,
                invoiceNumber: `INV-2025-${counters.invoiceNumber.toString().padStart(3, '0')}`,
                userId: memo.dealerId, // REAL dealer ID from memo ✅
                memoId: memo.id, // REAL memo ID ✅
                memoApprovedAt: memo.approvedAt || issueDate.toISOString(),
                customerId: memo.dealerId, // REAL dealer ID ✅
                customerEmail: memo.dealerEmail, // REAL email from memo ✅
                customerName: memo.dealerName, // REAL name from memo ✅
                customerBusinessName: memo.dealerBusinessName, // REAL business from memo ✅
                customerAddress: `${Math.floor(Math.random() * 9000 + 1000)} Main St, ${CITIES[i % CITIES.length]}, ${STATES[i % STATES.length]} ${Math.floor(Math.random() * 90000 + 10000)}`,
                customerPhone: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                status,
                issueDate: issueDate.toISOString(),
                dueDate: dueDate.toISOString(),
                paidDate,
                lineItems,
                subtotal,
                taxRate,
                taxAmount,
                total,
                amountPaid,
                amountDue: total - amountPaid,
                paymentTerms: 'Net 30',
                companyName: 'Royal Gems, Inc.',
                companyAddress: '123 Diamond Plaza, New York, NY 10001',
                companyPhone: '+1-800-ROYALGEM',
                companyEmail: 'billing@royalgems.com',
                companyTaxId: '12-3456789',
                notes: status === InvoiceStatus.PAID ? 'Thank you for your business!' : '',
                createdAt: issueDate.toISOString(),
                updatedAt: paidDate || issueDate.toISOString(),
                createdBy: 'MOCK_USER',
                history: [],
            });
            counters.invoiceId++;
            counters.invoiceNumber++;
        }
        this.updateCounters(counters);
        return invoices;
    }
    // ============================================
    // Storage Operations
    // ============================================
    saveSuppliers(suppliers) {
        localStorage.setItem(STORAGE_KEYS.SUPPLIERS, JSON.stringify(suppliers));
    }
    savePurchases(purchases) {
        localStorage.setItem(STORAGE_KEYS.PURCHASES, JSON.stringify(purchases));
    }
    savePayments(payments) {
        localStorage.setItem(STORAGE_KEYS.PAYMENTS, JSON.stringify(payments));
    }
    saveInvoices(invoices) {
        localStorage.setItem(STORAGE_KEYS.INVOICES, JSON.stringify(invoices));
    }
    // ============================================
    // Public API - Suppliers
    // ============================================
    getSuppliers() {
        const stored = localStorage.getItem(STORAGE_KEYS.SUPPLIERS);
        return stored ? JSON.parse(stored) : [];
    }
    getSupplier(id) {
        const suppliers = this.getSuppliers();
        return suppliers.find(s => s.id === id) || null;
    }
    createSupplier(data) {
        const suppliers = this.getSuppliers();
        const counters = this.getCounters();
        const newSupplier = {
            ...data,
            id: `SUP${counters.supplierId.toString().padStart(6, '0')}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        suppliers.push(newSupplier);
        this.saveSuppliers(suppliers);
        counters.supplierId++;
        this.updateCounters(counters);
        return newSupplier;
    }
    updateSupplier(id, data) {
        const suppliers = this.getSuppliers();
        const index = suppliers.findIndex(s => s.id === id);
        if (index === -1)
            return null;
        suppliers[index] = {
            ...suppliers[index],
            ...data,
            id: suppliers[index].id, // Preserve ID
            updatedAt: new Date().toISOString(),
        };
        this.saveSuppliers(suppliers);
        return suppliers[index];
    }
    deleteSupplier(id) {
        const suppliers = this.getSuppliers();
        const filtered = suppliers.filter(s => s.id !== id);
        if (filtered.length === suppliers.length)
            return false;
        this.saveSuppliers(filtered);
        return true;
    }
    // ============================================
    // Public API - Purchases
    // ============================================
    getPurchases() {
        const stored = localStorage.getItem(STORAGE_KEYS.PURCHASES);
        return stored ? JSON.parse(stored) : [];
    }
    getPurchase(id) {
        const purchases = this.getPurchases();
        return purchases.find(p => p.id === id) || null;
    }
    createPurchase(data) {
        const purchases = this.getPurchases();
        const counters = this.getCounters();
        const newPurchase = {
            ...data,
            id: `PUR${counters.purchaseId.toString().padStart(6, '0')}`,
            purchaseNumber: `PO-${counters.purchaseNumber}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        purchases.push(newPurchase);
        this.savePurchases(purchases);
        counters.purchaseId++;
        counters.purchaseNumber++;
        this.updateCounters(counters);
        return newPurchase;
    }
    updatePurchase(id, data) {
        const purchases = this.getPurchases();
        const index = purchases.findIndex(p => p.id === id);
        if (index === -1)
            return null;
        purchases[index] = {
            ...purchases[index],
            ...data,
            id: purchases[index].id, // Preserve ID
            purchaseNumber: purchases[index].purchaseNumber, // Preserve number
            updatedAt: new Date().toISOString(),
        };
        this.savePurchases(purchases);
        return purchases[index];
    }
    deletePurchase(id) {
        const purchases = this.getPurchases();
        const filtered = purchases.filter(p => p.id !== id);
        if (filtered.length === purchases.length)
            return false;
        this.savePurchases(filtered);
        return true;
    }
    // ============================================
    // Public API - Payments
    // ============================================
    getPayments() {
        const stored = localStorage.getItem(STORAGE_KEYS.PAYMENTS);
        return stored ? JSON.parse(stored) : [];
    }
    getPayment(id) {
        const payments = this.getPayments();
        return payments.find(p => p.id === id) || null;
    }
    createPayment(data) {
        const payments = this.getPayments();
        const counters = this.getCounters();
        const newPayment = {
            ...data,
            id: `PAY${counters.paymentId.toString().padStart(6, '0')}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        payments.push(newPayment);
        this.savePayments(payments);
        // Update associated purchase payment status
        if (data.referenceType === 'purchase' && data.referenceId) {
            const purchase = this.getPurchase(data.referenceId);
            if (purchase) {
                const newAmountPaid = purchase.amountPaid + data.amount;
                const newAmountOwed = purchase.total - newAmountPaid;
                const newPaymentStatus = newAmountOwed <= 0 ? 'paid' : (newAmountPaid > 0 ? 'partial' : 'unpaid');
                this.updatePurchase(data.referenceId, {
                    amountPaid: newAmountPaid,
                    amountOwed: newAmountOwed,
                    paymentStatus: newPaymentStatus,
                });
            }
        }
        counters.paymentId++;
        this.updateCounters(counters);
        return newPayment;
    }
    deletePayment(id) {
        const payments = this.getPayments();
        const payment = payments.find(p => p.id === id);
        if (!payment)
            return false;
        const filtered = payments.filter(p => p.id !== id);
        this.savePayments(filtered);
        // Update associated purchase payment status
        if (payment.referenceType === 'purchase' && payment.referenceId) {
            const purchase = this.getPurchase(payment.referenceId);
            if (purchase) {
                const newAmountPaid = Math.max(0, purchase.amountPaid - payment.amount);
                const newAmountOwed = purchase.total - newAmountPaid;
                const newPaymentStatus = newAmountOwed <= 0 ? 'paid' : (newAmountPaid > 0 ? 'partial' : 'unpaid');
                this.updatePurchase(payment.referenceId, {
                    amountPaid: newAmountPaid,
                    amountOwed: newAmountOwed,
                    paymentStatus: newPaymentStatus,
                });
            }
        }
        return true;
    }
    // ============================================
    // Public API - Invoices
    // ============================================
    getInvoices() {
        const stored = localStorage.getItem(STORAGE_KEYS.INVOICES);
        return stored ? JSON.parse(stored) : [];
    }
    getInvoice(id) {
        const invoices = this.getInvoices();
        return invoices.find(inv => inv.id === id) || null;
    }
    updateInvoice(id, data) {
        const invoices = this.getInvoices();
        const index = invoices.findIndex(inv => inv.id === id);
        if (index === -1)
            return null;
        invoices[index] = {
            ...invoices[index],
            ...data,
            id: invoices[index].id,
            invoiceNumber: invoices[index].invoiceNumber,
            updatedAt: new Date().toISOString(),
        };
        this.saveInvoices(invoices);
        return invoices[index];
    }
    deleteInvoice(id) {
        const invoices = this.getInvoices();
        const filtered = invoices.filter(inv => inv.id !== id);
        if (filtered.length === invoices.length)
            return false;
        this.saveInvoices(filtered);
        return true;
    }
    createInvoice(data) {
        const invoices = this.getInvoices();
        const now = new Date();
        const counters = this.getCounters();
        // Generate invoice number
        const invoiceNumber = `INV-${now.getFullYear()}-${String(counters.invoiceNumber + 1).padStart(4, '0')}`;
        // Update counters
        localStorage.setItem(STORAGE_KEYS.COUNTERS, JSON.stringify({ ...counters, invoiceNumber: counters.invoiceNumber + 1 }));
        // Calculate due date (default Net 30)
        const dueDate = new Date(now);
        dueDate.setDate(dueDate.getDate() + 30);
        const newInvoice = {
            id: `invoice-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            invoiceNumber,
            userId: data.customerId,
            memoId: data.memoId || '',
            memoApprovedAt: '',
            customerId: data.customerId,
            customerEmail: data.customerEmail,
            customerName: data.customerName,
            customerBusinessName: data.customerBusinessName,
            customerAddress: data.customerAddress,
            customerPhone: data.customerPhone,
            status: 'issued',
            issueDate: now.toISOString(),
            dueDate: dueDate.toISOString(),
            lineItems: data.lineItems.map((item, idx) => ({
                id: `line-${Date.now()}-${idx}`,
                ...item
            })),
            subtotal: data.subtotal,
            taxRate: data.taxRate,
            taxAmount: data.taxAmount,
            discountAmount: data.discountAmount,
            discountReason: data.discountReason,
            total: data.total,
            paymentTerms: data.paymentTerms || 'Net 30',
            notes: data.notes || 'Thank you for your business.',
            terms: 'All sales are final. Late payments subject to 1.5% monthly interest charge.',
            companyName: 'Royal Gems, Inc.',
            companyAddress: '123 Diamond District\nNew York, NY 10036',
            companyPhone: '(555) 123-4567',
            companyEmail: 'invoices@royalgemsusa.com',
            bankingInfo: {
                bankName: 'Chase Bank',
                accountNumber: '****1234',
                routingNumber: '021000021',
                accountType: 'Business Checking',
                swiftCode: 'CHASUS33'
            },
            createdAt: now.toISOString(),
            updatedAt: now.toISOString(),
            createdBy: 'admin',
            history: [{
                    timestamp: now.toISOString(),
                    action: 'Invoice created by admin',
                    performedBy: 'admin',
                    performedByRole: 'admin',
                    newStatus: 'issued'
                }]
        };
        invoices.push(newInvoice);
        this.saveInvoices(invoices);
        return newInvoice;
    }
    // ============================================
    // Public API - Memos
    // ============================================
    saveMemos(memos) {
        localStorage.setItem(STORAGE_KEYS.MEMOS, JSON.stringify(memos));
    }
    getMemos() {
        const stored = localStorage.getItem(STORAGE_KEYS.MEMOS);
        return stored ? JSON.parse(stored) : [];
    }
    getMemo(id) {
        const memos = this.getMemos();
        return memos.find(m => m.id === id) || null;
    }
    updateMemo(id, data) {
        const memos = this.getMemos();
        const index = memos.findIndex(m => m.id === id);
        if (index === -1)
            return null;
        memos[index] = {
            ...memos[index],
            ...data,
            id: memos[index].id,
            updatedAt: new Date().toISOString(),
        };
        this.saveMemos(memos);
        return memos[index];
    }
    createMemo(data) {
        const memos = this.getMemos();
        const now = new Date().toISOString();
        const newMemo = {
            id: `memo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            dealerId: data.dealerId,
            dealerEmail: data.dealerEmail,
            dealerName: data.dealerName,
            dealerBusinessName: data.dealerBusinessName,
            diamondId: data.diamondId,
            diamondDetails: data.diamondDetails,
            status: 'pending',
            requestedAt: now,
            clientName: data.clientName,
            clientContact: data.clientContact,
            purpose: data.purpose,
            dueDate: data.dueDate,
            dealerNotes: data.dealerNotes,
            history: [{
                    timestamp: now,
                    action: 'Memo created by admin',
                    performedBy: 'admin',
                    performedByRole: 'admin'
                }],
            createdAt: now,
            updatedAt: now
        };
        memos.push(newMemo);
        this.saveMemos(memos);
        return newMemo;
    }
    // ============================================
    // Public API - Customer Payments (A/R)
    // ============================================
    saveCustomerPayments(payments) {
        localStorage.setItem(STORAGE_KEYS.CUSTOMER_PAYMENTS, JSON.stringify(payments));
    }
    getCustomerPayments() {
        const stored = localStorage.getItem(STORAGE_KEYS.CUSTOMER_PAYMENTS);
        return stored ? JSON.parse(stored) : [];
    }
    getCustomerPayment(id) {
        const payments = this.getCustomerPayments();
        return payments.find(p => p.id === id) || null;
    }
    createCustomerPayment(data) {
        const payments = this.getCustomerPayments();
        const counters = this.getCounters();
        const newPayment = {
            ...data,
            id: `CPAY${counters.customerPaymentId.toString().padStart(6, '0')}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        payments.push(newPayment);
        this.saveCustomerPayments(payments);
        counters.customerPaymentId++;
        this.updateCounters(counters);
        return newPayment;
    }
    // ============================================
    // Public API - Diamonds
    // ============================================
    saveDiamonds(diamonds) {
        localStorage.setItem(STORAGE_KEYS.DIAMONDS, JSON.stringify(diamonds));
    }
    getDiamonds() {
        const stored = localStorage.getItem(STORAGE_KEYS.DIAMONDS);
        return stored ? JSON.parse(stored) : [];
    }
    getDiamond(id) {
        const diamonds = this.getDiamonds();
        return diamonds.find(d => d.id === id) || null;
    }
    // ============================================
    // Public API - Contacts (Phonebook)
    // ============================================
    /**
     * Generate realistic phonebook contacts
     * Mix of suppliers, customers, and unassigned contacts
     */
    generateContacts(count) {
        const contacts = [];
        const counters = this.getCounters();
        // Additional company names for contacts (beyond suppliers)
        const CONTACT_COMPANIES = [
            'Apex Diamond Group', 'Sterling Jewelers', 'Prestige Gems LLC', 'Cartier Collections',
            'Blue Nile Partners', 'Tiffany Trading Co', 'Harry Winston Ltd', 'De Beers Direct',
            'Graff Distribution', 'Chopard Imports', 'Bvlgari Supply', 'Van Cleef Associates',
            'Rolex Gemstones', 'Pandora Wholesale', 'Swarovski Trading', 'Mikimoto Pearl Co',
            'James Allen Inc', 'Brilliant Earth', 'Ritani Group', 'Adiamor Supply',
            'Whiteflash Diamonds', 'Brian Gavin', 'Good Old Gold', 'Victor Canera'
        ];
        for (let i = 0; i < count; i++) {
            const companyName = i < CONTACT_COMPANIES.length
                ? CONTACT_COMPANIES[i]
                : `${CONTACT_COMPANIES[i % CONTACT_COMPANIES.length]} ${Math.floor(i / CONTACT_COMPANIES.length) + 1}`;
            const firstName = CONTACT_FIRST_NAMES[Math.floor(Math.random() * CONTACT_FIRST_NAMES.length)];
            const lastName = CONTACT_LAST_NAMES[Math.floor(Math.random() * CONTACT_LAST_NAMES.length)];
            const cityIndex = i % CITIES.length;
            // Designation distribution - guaranteed mix with some fixed "both" contacts
            // First 4 contacts: guaranteed distribution (1 each)
            // Rest: random distribution
            let designation;
            let supplierId;
            let customerId;
            if (i === 0) {
                // First contact is always "both" - key partner
                designation = 'both';
                supplierId = `SUP${(counters.supplierId + i).toString().padStart(6, '0')}`;
                customerId = `USR${(1000 + i).toString().padStart(6, '0')}`;
            }
            else if (i === 1) {
                designation = 'supplier';
                supplierId = `SUP${(counters.supplierId + i).toString().padStart(6, '0')}`;
            }
            else if (i === 2) {
                designation = 'customer';
                customerId = `USR${(1000 + i).toString().padStart(6, '0')}`;
            }
            else if (i === 3) {
                // Second "both" contact
                designation = 'both';
                supplierId = `SUP${(counters.supplierId + i).toString().padStart(6, '0')}`;
                customerId = `USR${(1000 + i).toString().padStart(6, '0')}`;
            }
            else if (i === 8 || i === 15) {
                // Additional "both" contacts spread throughout
                designation = 'both';
                supplierId = `SUP${(counters.supplierId + i).toString().padStart(6, '0')}`;
                customerId = `USR${(1000 + i).toString().padStart(6, '0')}`;
            }
            else {
                // Random distribution for remaining: 35% supplier, 30% customer, 35% none
                const designationRoll = Math.random();
                if (designationRoll < 0.35) {
                    designation = 'supplier';
                    supplierId = `SUP${(counters.supplierId + i).toString().padStart(6, '0')}`;
                }
                else if (designationRoll < 0.65) {
                    designation = 'customer';
                    customerId = `USR${(1000 + i).toString().padStart(6, '0')}`;
                }
                else {
                    designation = 'none';
                }
            }
            // Status: 90% active, 10% inactive
            const status = Math.random() < 0.9 ? 'active' : 'inactive';
            // Tags based on designation and random attributes
            const tags = [];
            if (designation === 'supplier' || designation === 'both') {
                tags.push('supplier');
                if (Math.random() < 0.3)
                    tags.push('preferred');
            }
            if (designation === 'customer' || designation === 'both') {
                tags.push('customer');
                if (Math.random() < 0.2)
                    tags.push('VIP');
            }
            if (Math.random() < 0.15)
                tags.push('international');
            // Created in last 180 days
            const daysAgo = Math.floor(Math.random() * 180);
            const createdAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
            const updatedAt = new Date(Date.now() - Math.floor(Math.random() * Math.min(daysAgo, 30)) * 24 * 60 * 60 * 1000).toISOString();
            contacts.push({
                id: `CON${counters.contactId.toString().padStart(6, '0')}`,
                type: 'contact',
                companyName,
                contactPerson: `${firstName} ${lastName}`,
                email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${companyName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/gi, '')}.com`,
                phone: `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
                mobilePhone: Math.random() < 0.7 ? `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}` : undefined,
                fax: Math.random() < 0.3 ? `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}` : undefined,
                website: Math.random() < 0.6 ? `https://www.${companyName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/gi, '')}.com` : undefined,
                address: {
                    street: `${Math.floor(Math.random() * 9000 + 1000)} ${['Diamond', 'Gem', 'Jewelry', 'Crystal', 'Pearl'][Math.floor(Math.random() * 5)]} ${['Street', 'Avenue', 'Boulevard', 'Drive'][Math.floor(Math.random() * 4)]}`,
                    city: CITIES[cityIndex],
                    state: STATES[cityIndex],
                    zipCode: `${Math.floor(Math.random() * 90000 + 10000)}`,
                    country: 'USA',
                },
                designation,
                supplierId,
                customerId,
                notes: Math.random() < 0.3 ? `${designation === 'none' ? 'Potential lead - follow up required' : designation === 'both' ? 'Key partner - handles both supply and retail' : `Established ${designation} relationship`}` : undefined,
                tags,
                status,
                createdAt,
                updatedAt,
                createdBy: 'MOCK_USER',
            });
            counters.contactId++;
        }
        this.updateCounters(counters);
        return contacts;
    }
    saveContacts(contacts) {
        localStorage.setItem(STORAGE_KEYS.CONTACTS, JSON.stringify(contacts));
    }
    getContacts() {
        const stored = localStorage.getItem(STORAGE_KEYS.CONTACTS);
        return stored ? JSON.parse(stored) : [];
    }
    getContact(id) {
        const contacts = this.getContacts();
        return contacts.find(c => c.id === id) || null;
    }
    createContact(data) {
        const contacts = this.getContacts();
        const counters = this.getCounters();
        const newContact = {
            ...data,
            id: `CON${counters.contactId.toString().padStart(6, '0')}`,
            type: 'contact',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        contacts.push(newContact);
        this.saveContacts(contacts);
        counters.contactId++;
        this.updateCounters(counters);
        return newContact;
    }
    updateContact(id, data) {
        const contacts = this.getContacts();
        const index = contacts.findIndex(c => c.id === id);
        if (index === -1)
            return null;
        contacts[index] = {
            ...contacts[index],
            ...data,
            id: contacts[index].id, // Preserve ID
            type: 'contact', // Preserve type
            updatedAt: new Date().toISOString(),
        };
        this.saveContacts(contacts);
        return contacts[index];
    }
    deleteContact(id) {
        const contacts = this.getContacts();
        const filtered = contacts.filter(c => c.id !== id);
        if (filtered.length === contacts.length)
            return false;
        this.saveContacts(filtered);
        return true;
    }
    /**
     * Get contacts statistics for dashboard
     */
    getContactStatistics() {
        const contacts = this.getContacts();
        return {
            total: contacts.length,
            suppliers: contacts.filter(c => c.designation === 'supplier').length,
            customers: contacts.filter(c => c.designation === 'customer').length,
            both: contacts.filter(c => c.designation === 'both').length,
            unassigned: contacts.filter(c => c.designation === 'none').length,
            active: contacts.filter(c => c.status === 'active').length,
            inactive: contacts.filter(c => c.status === 'inactive').length,
        };
    }
    // ============================================
    // Auction Mock Data - Storage
    // ============================================
    getAuctions() {
        const stored = localStorage.getItem(STORAGE_KEYS.AUCTIONS);
        return stored ? JSON.parse(stored) : [];
    }
    saveAuctions(auctions) {
        localStorage.setItem(STORAGE_KEYS.AUCTIONS, JSON.stringify(auctions));
    }
    getAuctionBids() {
        const stored = localStorage.getItem(STORAGE_KEYS.AUCTION_BIDS);
        return stored ? JSON.parse(stored) : [];
    }
    saveAuctionBids(bids) {
        localStorage.setItem(STORAGE_KEYS.AUCTION_BIDS, JSON.stringify(bids));
    }
    getAuctionSummaries() {
        const stored = localStorage.getItem(STORAGE_KEYS.AUCTION_SUMMARIES);
        return stored ? JSON.parse(stored) : [];
    }
    saveAuctionSummaries(summaries) {
        localStorage.setItem(STORAGE_KEYS.AUCTION_SUMMARIES, JSON.stringify(summaries));
    }
    // ============================================
    // Auction Mock Data - CRUD
    // ============================================
    getAuction(auctionId) {
        const auctions = this.getAuctions();
        return auctions.find((a) => a.auctionId === auctionId) || null;
    }
    getAuctionSummary(auctionId) {
        const summaries = this.getAuctionSummaries();
        return summaries.find((s) => s.auctionId === auctionId) || null;
    }
    getAuctionBidsForAuction(auctionId) {
        const bids = this.getAuctionBids();
        return bids.filter((b) => b.auctionId === auctionId).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
    createAuction(input) {
        const counters = this.getCounters();
        const auctionId = `AUC${counters.auctionId.toString().padStart(6, '0')}`;
        counters.auctionId++;
        this.updateCounters(counters);
        const now = new Date().toISOString();
        const auction = {
            id: auctionId,
            auctionId,
            type: 'auction',
            diamondId: input.diamondId,
            diamondKey1: input.diamondKey1,
            diamondStockNumber: null,
            diamondSnapshot: input.diamondSnapshot || {
                shape: 'Round',
                caratWeight: 1.5,
                color: 'G',
                clarity: 'VS1',
                lab: 'GIA',
                certificateNumber: 'GIA' + Math.floor(Math.random() * 9000000 + 1000000),
                imageUrl: '',
                totalPrice: input.startingPrice * 1.5,
                stockNumber: input.diamondId,
            },
            auctionType: input.auctionType,
            visibility: input.visibility || 'public',
            minimumDealerTier: input.minimumDealerTier || 1,
            startingPrice: input.startingPrice,
            minimumIncrementPercent: input.minimumIncrementPercent || 2,
            reservePrice: input.reservePrice || null,
            scheduledStartTime: input.scheduledStartTime,
            scheduledEndTime: input.scheduledEndTime,
            actualEndTime: null,
            gracePeriodHours: input.gracePeriodHours || 1,
            graceExtensionCount: 0,
            maxGraceExtensions: input.maxGraceExtensions || 3,
            status: 'scheduled',
            currentBid: 0,
            currentBidderId: null,
            bidCount: 0,
            supervisedState: input.auctionType === 'supervised' ? 'draft' : null,
            lootMasterId: 'mock-admin-001',
            invitedDealerIds: input.invitedDealerIds || [],
            createdBy: 'mock-admin-001',
            createdAt: now,
            updatedAt: now,
        };
        const auctions = this.getAuctions();
        auctions.push(auction);
        this.saveAuctions(auctions);
        // Create summary
        const summary = {
            id: `${auctionId}-summary`,
            auctionId,
            type: 'auction-summary',
            currentBid: 0,
            currentBidderAnonymousId: null,
            bidCount: 0,
            uniqueBidderCount: 0,
            endTime: input.scheduledEndTime,
            status: 'scheduled',
            graceExtended: false,
            graceExtensionCount: 0,
            supervisedState: input.auctionType === 'supervised' ? 'draft' : null,
            recentBids: [],
            updatedAt: now,
        };
        const summaries = this.getAuctionSummaries();
        summaries.push(summary);
        this.saveAuctionSummaries(summaries);
        return auction;
    }
    updateAuction(auctionId, updates) {
        const auctions = this.getAuctions();
        const index = auctions.findIndex((a) => a.auctionId === auctionId);
        if (index === -1)
            return null;
        auctions[index] = { ...auctions[index], ...updates, updatedAt: new Date().toISOString() };
        this.saveAuctions(auctions);
        return auctions[index];
    }
    cancelAuction(auctionId) {
        const auction = this.updateAuction(auctionId, { status: 'cancelled', actualEndTime: new Date().toISOString() });
        if (auction) {
            const summaries = this.getAuctionSummaries();
            const sIdx = summaries.findIndex((s) => s.auctionId === auctionId);
            if (sIdx !== -1) {
                summaries[sIdx].status = 'cancelled';
                summaries[sIdx].updatedAt = new Date().toISOString();
                this.saveAuctionSummaries(summaries);
            }
            return true;
        }
        return false;
    }
    placeBid(auctionId, amount, bidderId) {
        const auction = this.getAuction(auctionId);
        if (!auction)
            return { success: false, error: 'Auction not found' };
        if (auction.status !== 'active' && auction.status !== 'grace_period') {
            return { success: false, error: 'Auction is not accepting bids' };
        }
        const counters = this.getCounters();
        const bidId = `BID${counters.bidId.toString().padStart(8, '0')}`;
        counters.bidId++;
        this.updateCounters(counters);
        const actualBidderId = bidderId || 'mock-dealer-001';
        const anonymousId = `Bidder #${Math.floor(Math.random() * 50) + 1}`;
        const now = new Date().toISOString();
        const bid = {
            id: bidId,
            auctionId,
            type: 'bid',
            bidderId: actualBidderId,
            bidderAnonymousId: anonymousId,
            amount,
            previousBid: auction.currentBid || auction.startingPrice,
            incrementPercent: auction.minimumIncrementPercent,
            source: 'platform',
            adminEnteredBy: null,
            status: 'active',
            timestamp: now,
            triggeredGraceExtension: false,
        };
        // Mark previous winning bid as outbid
        const bids = this.getAuctionBids();
        bids.forEach((b) => {
            if (b.auctionId === auctionId && b.status === 'active') {
                b.status = 'outbid';
            }
        });
        bids.push(bid);
        this.saveAuctionBids(bids);
        // Update auction
        this.updateAuction(auctionId, {
            currentBid: amount,
            currentBidderId: actualBidderId,
            bidCount: auction.bidCount + 1,
        });
        // Update summary
        const summaries = this.getAuctionSummaries();
        const sIdx = summaries.findIndex((s) => s.auctionId === auctionId);
        if (sIdx !== -1) {
            const recentBids = [
                { anonymousId, amount, timestamp: now },
                ...summaries[sIdx].recentBids,
            ].slice(0, 10);
            summaries[sIdx] = {
                ...summaries[sIdx],
                currentBid: amount,
                currentBidderAnonymousId: anonymousId,
                bidCount: summaries[sIdx].bidCount + 1,
                uniqueBidderCount: summaries[sIdx].uniqueBidderCount + (Math.random() > 0.5 ? 1 : 0),
                recentBids,
                updatedAt: now,
            };
            this.saveAuctionSummaries(summaries);
        }
        return {
            success: true,
            bidId,
            newCurrentBid: amount,
            anonymousId,
            graceExtended: false,
        };
    }
    getMyBids(_bidderId) {
        const bids = this.getAuctionBids();
        // In mock mode, return all bids as "my bids" for demo purposes
        const myBids = bids.filter((b) => b.bidderId === 'mock-dealer-001');
        return myBids.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
    supervisedActionMock(auctionId, action, _payload) {
        const stateTransitions = {
            'open_bidding': 'accepting_bids',
            'pause_bidding': 'paused',
            'going_once': 'going_once',
            'going_twice': 'going_twice',
            'sold': 'sold',
            'no_sale': 'closed',
        };
        const newState = stateTransitions[action] || 'draft';
        const auction = this.getAuction(auctionId);
        if (!auction)
            return { success: false, error: 'Auction not found' };
        const newStatus = (action === 'sold' || action === 'no_sale') ? 'closed' : auction.status;
        this.updateAuction(auctionId, {
            supervisedState: newState,
            status: newStatus,
            ...(action === 'sold' ? { winnerId: auction.currentBidderId, finalPrice: auction.currentBid, actualEndTime: new Date().toISOString() } : {}),
            ...(action === 'no_sale' ? { actualEndTime: new Date().toISOString() } : {}),
        });
        // Update summary
        const summaries = this.getAuctionSummaries();
        const sIdx = summaries.findIndex((s) => s.auctionId === auctionId);
        if (sIdx !== -1) {
            summaries[sIdx].supervisedState = newState;
            summaries[sIdx].status = newStatus;
            if (action === 'sold') {
                summaries[sIdx].finalPrice = auction.currentBid;
                summaries[sIdx].winnerAnonymousId = summaries[sIdx].currentBidderAnonymousId;
                summaries[sIdx].reserveMet = true;
            }
            summaries[sIdx].updatedAt = new Date().toISOString();
            this.saveAuctionSummaries(summaries);
        }
        return { success: true, data: { newState } };
    }
    getAuctionAnalytics() {
        const auctions = this.getAuctions();
        const closed = auctions.filter((a) => a.status === 'closed');
        const active = auctions.filter((a) => a.status === 'active' || a.status === 'grace_period');
        const bids = this.getAuctionBids();
        const avgFinalPrice = closed.length > 0
            ? closed.reduce((sum, a) => sum + (a.finalPrice || a.currentBid || 0), 0) / closed.length
            : 0;
        const avgBids = auctions.length > 0
            ? bids.length / auctions.length
            : 0;
        const uniqueBidders = new Set(bids.map((b) => b.bidderId)).size;
        return {
            totalAuctions: auctions.length,
            avgFinalPrice: Math.round(avgFinalPrice),
            avgBidCount: Math.round(avgBids * 10) / 10,
            conversionRate: auctions.length > 0 ? Math.round((closed.filter((a) => a.winnerId).length / Math.max(closed.length, 1)) * 100) : 0,
            activeBidderCount: uniqueBidders,
        };
    }
    // ============================================
    // Auction Mock Data - Generation
    // ============================================
    generateAuctionData() {
        const auctions = [];
        const allBids = [];
        const summaries = [];
        const counters = this.getCounters();
        const now = new Date();
        // Diamond showcase data for auctions
        const auctionDiamonds = [
            { shape: 'Round', caratWeight: 3.21, color: 'D', clarity: 'VVS1', lab: 'GIA', cert: 'GIA2391847', price: 95000, stock: 'RG-D001' },
            { shape: 'Emerald', caratWeight: 4.55, color: 'E', clarity: 'VS1', lab: 'GIA', cert: 'GIA7482910', price: 82000, stock: 'RG-D002' },
            { shape: 'Oval', caratWeight: 2.78, color: 'F', clarity: 'VS2', lab: 'GIA', cert: 'GIA5193027', price: 45000, stock: 'RG-D003' },
            { shape: 'Cushion', caratWeight: 5.02, color: 'G', clarity: 'SI1', lab: 'AGS', cert: 'AGS104820397', price: 68000, stock: 'RG-D004' },
            { shape: 'Pear', caratWeight: 1.95, color: 'D', clarity: 'IF', lab: 'GIA', cert: 'GIA8301948', price: 55000, stock: 'RG-D005' },
            { shape: 'Marquise', caratWeight: 2.10, color: 'E', clarity: 'VVS2', lab: 'HRD', cert: 'HRD20481', price: 38000, stock: 'RG-D006' },
            { shape: 'Princess', caratWeight: 3.88, color: 'H', clarity: 'VS1', lab: 'IGI', cert: 'IGI4827194', price: 52000, stock: 'RG-D007' },
            { shape: 'Radiant', caratWeight: 4.12, color: 'F', clarity: 'VS2', lab: 'GIA', cert: 'GIA6291043', price: 72000, stock: 'RG-D008' },
            { shape: 'Asscher', caratWeight: 2.45, color: 'G', clarity: 'VVS1', lab: 'GIA', cert: 'GIA3049182', price: 48000, stock: 'RG-D009' },
            { shape: 'Heart', caratWeight: 1.82, color: 'D', clarity: 'VS1', lab: 'GIA', cert: 'GIA9410283', price: 42000, stock: 'RG-D010' },
        ];
        // Bidder names for realistic bid history
        const bidderNames = [
            'Bidder #7', 'Bidder #12', 'Bidder #3', 'Bidder #18', 'Bidder #25',
            'Bidder #9', 'Bidder #31', 'Bidder #15', 'Bidder #22', 'Bidder #5',
        ];
        const bidderIds = [
            'dealer-001', 'dealer-002', 'dealer-003', 'dealer-004', 'dealer-005',
            'dealer-006', 'dealer-007', 'dealer-008', 'dealer-009', 'dealer-010',
        ];
        // Scenario definitions
        const scenarios = [
            // 1. Live automated - ending soon, hot bidding
            { status: 'active', auctionType: 'automated', supervisedState: null, startOffset: -48, endOffset: 2, bidCount: 14, bidIncrementRange: [2, 8] },
            // 2. Live automated - just started, few bids
            { status: 'active', auctionType: 'automated', supervisedState: null, startOffset: -6, endOffset: 72, bidCount: 3, bidIncrementRange: [2, 5] },
            // 3. Live supervised - accepting bids
            { status: 'active', auctionType: 'supervised', supervisedState: 'accepting_bids', startOffset: -2, endOffset: 4, bidCount: 8, bidIncrementRange: [3, 10] },
            // 4. Live supervised - going once (dramatic moment)
            { status: 'active', auctionType: 'supervised', supervisedState: 'going_once', startOffset: -5, endOffset: 1, bidCount: 11, bidIncrementRange: [2, 7] },
            // 5. Scheduled - starts tomorrow
            { status: 'scheduled', auctionType: 'automated', supervisedState: null, startOffset: 24, endOffset: 96, bidCount: 0, bidIncrementRange: [0, 0] },
            // 6. Scheduled supervised - starts in 3 days
            { status: 'scheduled', auctionType: 'supervised', supervisedState: 'draft', startOffset: 72, endOffset: 144, bidCount: 0, bidIncrementRange: [0, 0] },
            // 7. Closed - sold with high bids
            { status: 'closed', auctionType: 'automated', supervisedState: null, startOffset: -168, endOffset: -96, bidCount: 22, bidIncrementRange: [2, 6], winnerId: 'dealer-003', finalPrice: 98500 },
            // 8. Closed supervised - sold
            { status: 'closed', auctionType: 'supervised', supervisedState: 'sold', startOffset: -120, endOffset: -72, bidCount: 16, bidIncrementRange: [3, 8], winnerId: 'dealer-007', finalPrice: 78200 },
            // 9. Closed - no sale (reserve not met)
            { status: 'closed', auctionType: 'automated', supervisedState: null, startOffset: -240, endOffset: -168, bidCount: 5, bidIncrementRange: [2, 3] },
            // 10. Cancelled
            { status: 'cancelled', auctionType: 'automated', supervisedState: null, startOffset: -48, endOffset: -24, bidCount: 2, bidIncrementRange: [2, 4] },
        ];
        scenarios.forEach((scenario, idx) => {
            const diamond = auctionDiamonds[idx];
            const auctionId = `AUC${counters.auctionId.toString().padStart(6, '0')}`;
            counters.auctionId++;
            const startTime = new Date(now.getTime() + scenario.startOffset * 3600000);
            const endTime = new Date(now.getTime() + scenario.endOffset * 3600000);
            const startingPrice = Math.round(diamond.price * 0.6);
            // Generate bids
            const auctionBids = [];
            let currentBid = 0;
            let currentBidderId = null;
            let currentBidderAnonymousId = null;
            for (let b = 0; b < scenario.bidCount; b++) {
                const bidId = `BID${counters.bidId.toString().padStart(8, '0')}`;
                counters.bidId++;
                const bidderIdx = b % bidderIds.length;
                const incrementPct = scenario.bidIncrementRange[0] + Math.random() * (scenario.bidIncrementRange[1] - scenario.bidIncrementRange[0]);
                const prevBid = currentBid || startingPrice;
                const bidAmount = b === 0 ? startingPrice : Math.round(prevBid * (1 + incrementPct / 100));
                // Spread bids across the auction duration
                const bidProgress = scenario.bidCount > 1 ? b / (scenario.bidCount - 1) : 0;
                const bidTime = new Date(startTime.getTime() + bidProgress * (endTime.getTime() - startTime.getTime()) * 0.9);
                currentBid = bidAmount;
                currentBidderId = bidderIds[bidderIdx];
                currentBidderAnonymousId = bidderNames[bidderIdx];
                const bid = {
                    id: bidId,
                    auctionId,
                    type: 'bid',
                    bidderId: bidderIds[bidderIdx],
                    bidderAnonymousId: bidderNames[bidderIdx],
                    amount: bidAmount,
                    previousBid: prevBid,
                    incrementPercent: Math.round(incrementPct * 10) / 10,
                    source: 'platform',
                    adminEnteredBy: null,
                    status: b === scenario.bidCount - 1 ? 'active' : 'outbid',
                    timestamp: bidTime.toISOString(),
                    triggeredGraceExtension: false,
                };
                auctionBids.push(bid);
            }
            // Override for scenario 7 & 8 closed with specific final price
            if (scenario.finalPrice) {
                currentBid = scenario.finalPrice;
                if (auctionBids.length > 0) {
                    auctionBids[auctionBids.length - 1].amount = scenario.finalPrice;
                    auctionBids[auctionBids.length - 1].status = 'winner';
                }
            }
            // Set winner bid status
            if (scenario.winnerId && auctionBids.length > 0) {
                auctionBids[auctionBids.length - 1].bidderId = scenario.winnerId;
                auctionBids[auctionBids.length - 1].status = 'winner';
            }
            const auction = {
                id: auctionId,
                auctionId,
                type: 'auction',
                diamondId: diamond.stock,
                diamondKey1: diamond.stock,
                diamondStockNumber: diamond.stock,
                diamondSnapshot: {
                    shape: diamond.shape,
                    caratWeight: diamond.caratWeight,
                    color: diamond.color,
                    clarity: diamond.clarity,
                    lab: diamond.lab,
                    certificateNumber: diamond.cert,
                    imageUrl: '',
                    totalPrice: diamond.price,
                    stockNumber: diamond.stock,
                },
                auctionType: scenario.auctionType,
                visibility: 'public',
                minimumDealerTier: 1,
                startingPrice,
                minimumIncrementPercent: 2,
                reservePrice: scenario.status === 'closed' && !scenario.winnerId ? diamond.price * 1.2 : null,
                scheduledStartTime: startTime.toISOString(),
                scheduledEndTime: endTime.toISOString(),
                actualEndTime: (scenario.status === 'closed' || scenario.status === 'cancelled') ? endTime.toISOString() : null,
                gracePeriodHours: 1,
                graceExtensionCount: 0,
                maxGraceExtensions: 3,
                status: scenario.status,
                currentBid: currentBid,
                currentBidderId: currentBidderId,
                bidCount: scenario.bidCount,
                supervisedState: scenario.supervisedState,
                lootMasterId: 'mock-admin-001',
                invitedDealerIds: [],
                winnerId: scenario.winnerId || undefined,
                finalPrice: scenario.finalPrice || (scenario.status === 'closed' ? currentBid : undefined),
                createdBy: 'mock-admin-001',
                createdAt: new Date(startTime.getTime() - 86400000).toISOString(),
                updatedAt: endTime.toISOString(),
            };
            const recentBids = auctionBids.slice(-10).reverse().map((b) => ({
                anonymousId: b.bidderAnonymousId,
                amount: b.amount,
                timestamp: b.timestamp,
            }));
            const uniqueBidderCount = new Set(auctionBids.map((b) => b.bidderId)).size;
            const summary = {
                id: `${auctionId}-summary`,
                auctionId,
                type: 'auction-summary',
                currentBid: currentBid,
                currentBidderAnonymousId: currentBidderAnonymousId,
                bidCount: scenario.bidCount,
                uniqueBidderCount,
                endTime: endTime.toISOString(),
                status: scenario.status,
                graceExtended: false,
                graceExtensionCount: 0,
                supervisedState: scenario.supervisedState,
                recentBids,
                winnerAnonymousId: scenario.winnerId ? (currentBidderAnonymousId || 'Bidder #3') : undefined,
                finalPrice: scenario.finalPrice || (scenario.status === 'closed' ? currentBid : undefined),
                reserveMet: scenario.winnerId ? true : (scenario.status === 'closed' && !scenario.winnerId ? false : undefined),
                updatedAt: endTime.toISOString(),
            };
            auctions.push(auction);
            allBids.push(...auctionBids);
            summaries.push(summary);
        });
        this.updateCounters(counters);
        return { auctions, bids: allBids, summaries };
    }
    // ============================================
    // Shipping Mock Mode (independent from Financial Hub)
    // ============================================
    isShippingMockMode() {
        return localStorage.getItem(STORAGE_KEYS.SHIPPING_MOCK_MODE) === 'true';
    }
    toggleShippingMockMode() {
        const newMode = !this.isShippingMockMode();
        if (newMode) {
            localStorage.setItem(STORAGE_KEYS.SHIPPING_MOCK_MODE, 'true');
            this.seedShippingData();
        }
        else {
            localStorage.setItem(STORAGE_KEYS.SHIPPING_MOCK_MODE, 'false');
            this.clearShippingData();
        }
        return newMode;
    }
    seedShippingData() {
        try {
            const { MOCK_SHIPMENTS } = require('../pages/Admin/IFSShippingDashboard/mockShipmentData');
            localStorage.setItem(STORAGE_KEYS.SHIPPING_SHIPMENTS, JSON.stringify(MOCK_SHIPMENTS));
        }
        catch (e) {
            console.warn('Failed to seed shipping data:', e);
        }
    }
    clearShippingData() {
        localStorage.removeItem(STORAGE_KEYS.SHIPPING_SHIPMENTS);
    }
    getShipments() {
        const stored = localStorage.getItem(STORAGE_KEYS.SHIPPING_SHIPMENTS);
        return stored ? JSON.parse(stored) : [];
    }
    getShipment(id) {
        return this.getShipments().find((s) => s.id === id);
    }
    createShipment(data) {
        const shipments = this.getShipments();
        const now = new Date().toISOString();
        const newId = `ship_mock_${Date.now()}`;
        const shipment = {
            userId: 'mock-user',
            type: 'shipment',
            status: data.status || 'draft',
            source: data.source || 'wizard',
            trackingNumber: data.trackingNumber || null,
            shipmentId: data.shipmentId || null,
            confirmationNumber: data.confirmationNumber || null,
            sender: data.sender || {},
            recipient: data.recipient || {},
            package: data.package || {},
            cost: data.cost || {},
            delivery: data.delivery || {},
            documents: data.documents || {},
            createdBy: 'mock-user',
            createdAt: now,
            updatedAt: now,
            ...data,
            id: newId,
        };
        shipments.unshift(shipment);
        localStorage.setItem(STORAGE_KEYS.SHIPPING_SHIPMENTS, JSON.stringify(shipments));
        return shipment;
    }
    updateShipment(id, data) {
        const shipments = this.getShipments();
        const index = shipments.findIndex((s) => s.id === id);
        if (index === -1)
            return null;
        shipments[index] = { ...shipments[index], ...data, id, updatedAt: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEYS.SHIPPING_SHIPMENTS, JSON.stringify(shipments));
        return shipments[index];
    }
    deleteShipment(id) {
        const shipments = this.getShipments();
        const index = shipments.findIndex((s) => s.id === id);
        if (index === -1)
            return false;
        if (shipments[index].status !== 'draft')
            return false;
        shipments.splice(index, 1);
        localStorage.setItem(STORAGE_KEYS.SHIPPING_SHIPMENTS, JSON.stringify(shipments));
        return true;
    }
}
export default MockDataService.getInstance();
